import { useState, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, CloudSun, X, Plus, Minus, Settings, User, Bell, LayoutGrid, Home, Star, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Smartphone, QrCode, Gamepad2, PenTool, Code, Leaf, Palette, Heart, Music, MessageCircle, Briefcase, ShoppingBag, Target, GraduationCap, BookOpen, ThumbsUp, Crosshair, Move, Image, Camera, Layout, Sidebar, Shapes, Cloud, RotateCcw, MessageSquare, Info, Film, Languages, Package, Check, Globe, AlertCircle, Maximize2, Minimize2, Maximize, Minimize, PanelBottom, Trash2, Loader, Mail, ArrowLeft } from 'lucide-react'
import { Lunar } from 'lunar-javascript'
import './App.css'
import WidgetModal from './components/ui/WidgetModal'
import { WeatherWidget, WeatherModal, WidgetRenderer } from './components/widgets'
import { IconMap, getIconByName, getIconName } from './constants/icons'
import { PRESET_GROUP_TYPES, getChannel, presetMenus, AVAILABLE_WIDGETS, AVAILABLE_SHORTCUTS, aiEngines, searchEngines as defaultSearchEngines, defaultMenuItems, defaultPageData, recommendShortcuts, baseShortcuts, gameShortcuts, designShortcuts, devShortcuts, guessSearches, searchRecommendShortcuts, fetchPresets, sortLayoutItems } from './constants/presets'
import FlipDigit from './components/ui/FlipDigit'
import SettingsSwitch from './components/ui/SettingsSwitch'
import SettingsSlider from './components/ui/SettingsSlider'
import HotSearchModal from './components/widgets/HotSearchWidget/HotSearchModal'
import SettingsContent from './components/settings/SettingsContent'
import LibraryModal from './components/ui/LibraryModal'
import SearchEngineModal from './components/ui/SearchEngineModal'
import { useAutoBackup } from './hooks/useAutoBackup'
import { useAuth } from './contexts/AuthContext'
import { recordActionLog } from './utils/logger'
export const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v|flv|mkv)$/i.test(cleanUrl);
};

const generateSearchUrl = (engine, query) => {
  const url = engine?.url || engine?.searchUrl || 'https://www.baidu.com/s?wd=';
  const encodedQuery = encodeURIComponent(query);
  if (url.includes('%s')) {
    return url.replace('%s', encodedQuery);
  }
  return url + encodedQuery;
};

const getWidgetDefaultName = (widgetType) => {
  if (!widgetType || widgetType === 'weather') return '天气预报';
  if (widgetType === 'hotsearch') return '热搜榜';
  let defaultName = '小组件';
  AVAILABLE_WIDGETS.forEach(cat => {
    const found = cat.items.find(w => w.widgetType === widgetType);
    if (found) defaultName = found.name;
  });
  return defaultName;
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (custom) => {
    const i = typeof custom === 'number' ? custom : custom.i;
    const isPageTurn = typeof custom === 'object' ? custom.isPageTurn : true;
    return {
      opacity: 1, 
      scale: 1,
      transition: {
        delay: isPageTurn ? i * 0.03 : 0,
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    };
  },
  flipped: {
    opacity: 0,
    rotateY: 180,
    scale: 0.8,
    transition: { duration: 0.4, ease: "easeInOut" }
  }
};

function App() {
  const [uid] = useState(() => {
    let id = localStorage.getItem('visitor_uid');
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : 'uid-' + Date.now();
      localStorage.setItem('visitor_uid', id);
    }
    return id;
  });

  const [backupData] = useState(() => {
    try {
      const data = localStorage.getItem(`tab365_visitor_backup_${uid}`);
      return data ? JSON.parse(data) : null;
    } catch(e) { return null; }
  });

  const [time, setTime] = useState(new Date());
  const [searchEngines, setSearchEngines] = useState(defaultSearchEngines);
  const [currentPage, setCurrentPage] = useState(0);
  const prevCurrentPage = useRef(-1);
  const isPageTurn = currentPage !== prevCurrentPage.current;
  useEffect(() => {
    const handleScroll = (e) => {
      const target = e.target;
      if (target && target.classList && target.classList.contains('hover-scrollbar')) {
        target.classList.add('is-scrolling');
        if (target._scrollTimeout) clearTimeout(target._scrollTimeout);
        target._scrollTimeout = setTimeout(() => {
          target.classList.remove('is-scrolling');
        }, 800);
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);


  useEffect(() => {
    prevCurrentPage.current = currentPage;
  }, [currentPage]);

  // Report page_load action log once on initial mount
  const hasReportedPageLoad = useRef(false);
  useEffect(() => {
    if (!hasReportedPageLoad.current) {
      hasReportedPageLoad.current = true;
      const channel = getChannel();
      recordActionLog(
        'page_load',
        'page',
        'home',
        {
          channel,
          url: window.location.href,
          title: document.title || '365tab'
        },
        document.title || '365Tab 新标签页'
      );
    }
  }, []);

  const [isScrolling, setIsScrolling] = useState(false);
  const [isDockVisible, setIsDockVisible] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [activeWidgetPopup, setActiveWidgetPopup] = useState(null);
  const [loginTab, setLoginTab] = useState('phone'); // 'phone' or 'wechat'
  
  // Rolling Hot Search State
  const [rollingHotSearches, setRollingHotSearches] = useState([]);
  const [hotSearchRanking, setHotSearchRanking] = useState(null);
  const [currentRollingIndex, setCurrentRollingIndex] = useState(0);
  const searchBoxRef = useRef(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const [isDeleteGroupMode, setIsDeleteGroupMode] = useState(false);
  const [deleteConfirmGroupId, setDeleteConfirmGroupId] = useState(null);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [addGroupTab, setAddGroupTab] = useState('preset'); // 'preset' | 'custom'
  const [searchGroupTerm, setSearchGroupTerm] = useState('');
  const [activeGroupCategory, setActiveGroupCategory] = useState('全部');
  const [newGroupName, setNewGroupName] = useState('');
  const [isAddGroupMaximized, setIsAddGroupMaximized] = useState(false);
  const [selectedIconIndex, setSelectedIconIndex] = useState(0);
  const [addMenuOpenId, setAddMenuOpenId] = useState(null);
  const { authToken, setAuthToken, userInfo, setUserInfo } = useAuth();
  const [wechatConfig, setWechatConfig] = useState(null);
  const [syncConflict, setSyncConflict] = useState(null);
  
  // Messages State
  const [messages, setMessages] = useState([]);
  const [isMessageCenterOpen, setIsMessageCenterOpen] = useState(false);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [msgPage, setMsgPage] = useState(1);
  const unreadCount = messages.filter(m => m.isRead === 0).length;
  const [isQuickSettingsCollapsed, setIsQuickSettingsCollapsed] = useState(false);

  useEffect(() => {
    if (authToken) {
      fetch('/api/v1/message/my', {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data.code === 200 && data.data) {
          setMessages(data.data);
        }
      })
      .catch(console.error);
    } else {
      setMessages([]);
    }
  }, [authToken]);

  const handleMessageRead = async (message) => {
    if (message.isRead === 1) return;
    try {
      await fetch('/api/v1/message/read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ messageId: message.id })
      });
      setMessages(prev => prev.map(m => m.id === message.id ? { ...m, isRead: 1 } : m));
    } catch(e) {
      console.error('Failed to mark message as read', e);
    }
  };

  // Global Weather State
  const [globalWeatherData, setGlobalWeatherData] = useState(null);

  useEffect(() => {
    const fetchWeather = async (lon, lat) => {
      try {
        const url = (lon && lat) ? `/api/v1/weather/now?lon=${lon}&lat=${lat}` : `/api/v1/weather/now`;
        const response = await fetch(url);
        const res = await response.json();
        if (res.code === 200 && res.data) {
          setGlobalWeatherData(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch global weather", error);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => fetchWeather(position.coords.longitude, position.coords.latitude),
        (error) => {
          console.warn("Geolocation denied or error, falling back to IP based location.", error);
          fetchWeather(null, null);
        },
        { timeout: 5000 }
      );
    } else {
      fetchWeather(null, null);
    }
  }, []);
  
  const [dockItems, setDockItems] = useState(() => {
    try {
      const saved = localStorage.getItem('tab365_dock_items');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map(site => {
            if (site.icon && typeof site.icon === 'object') {
              site.icon = site.iconName ? getIconByName(site.iconName) : getIconByName('Globe');
            }
            return site;
          });
        }
      }
    } catch (e) {}
    return recommendShortcuts;
  });

  useEffect(() => {
    localStorage.setItem('tab365_dock_items', JSON.stringify(dockItems));
  }, [dockItems]);

  useEffect(() => {
    if ((!dockItems || dockItems.length === 0) && recommendShortcuts && recommendShortcuts.length > 0) {
      setDockItems(recommendShortcuts);
    }
  }, []);

  const [recentClicks, setRecentClicks] = useState(() => {
    try {
      const clicks = JSON.parse(localStorage.getItem('tab365_recent_clicks')) || [];
      return clicks.map(site => {
        if (site.icon && typeof site.icon === 'object') {
          // It was stringified, try to recover from iconName or default to a globe/string
          if (site.iconName) {
            site.icon = getIconByName(site.iconName);
          } else {
            site.icon = getIconByName('Globe'); // Default icon
          }
        }
        return site;
      });
    } catch (e) {
      return [];
    }
  });

  const handleWidgetClick = (iconItem) => {
    const wType = iconItem.widgetType || 'weather';
    const widgetNameMap = {
      weather: '天气预报',
      hotSearch: '热搜榜',
      calendar: '日历',
      calculator: '计算器'
    };
    const wName = iconItem.name || widgetNameMap[wType] || wType;
    const wId = iconItem.id || wType;
    recordActionLog('click_widget', 'widget', wId, { widgetType: wType, name: wName }, wName);
    setActiveWidgetPopup(wType);
  };

  const handleShortcutClick = (site) => {
    let sName = (site.name || site.title || '').trim();
    if (!sName && site.url) {
      try {
        const u = new URL(site.url);
        sName = u.hostname;
      } catch (e) {
        sName = site.url;
      }
    }
    if (!sName) {
      sName = '快捷导航';
    }
    const sId = site.id || sName || 'shortcut';
    recordActionLog('click_shortcut', 'shortcut', sId, { url: site.url, name: sName }, sName);
    window.open(site.url, '_blank');
    setRecentClicks(prev => {
      const newClicks = prev.filter(item => item.url !== site.url);
      const siteToSave = { ...site };
      if (typeof site.icon === 'object' && !site.iconName) {
        siteToSave.iconName = getIconName(site.icon);
      }
      newClicks.unshift(siteToSave);
      const topClicks = newClicks.slice(0, 10);
      localStorage.setItem('tab365_recent_clicks', JSON.stringify(topClicks));
      return topClicks;
    });
  };

  const defaultSettings = {
    iconStyle: 'default', iconSize: 72, iconRadius: 50, iconOpacity: 100,
    iconSync: true, iconDistance: 30, iconNameVisible: true, iconBackgroundVisible: true, iconFontSize: 20, iconColor: '#ffffff',
    iconMaxWidthUnit: 'px', iconMaxWidth: 1350,
    enableSearchHistory: true, enableGuessSearch: true, enableHotSearchRanking: true, enableSearchRecommend: true,
    themeMode: 'dark', themeFollowSystem: false, themeColor: '#3b82f6', wallpaperOpacity: 0, wallpaperBlur: 0, autoWallpaper: 'none',
    defaultLightWallpaper: '', defaultDarkWallpaper: '',
    layoutMode: 'widget',
    sidebarPosition: 'left', sidebarAutoHide: false, sidebarRemember: false, sidebarWheel: true, sidebarOpacity: 40, sidebarWidth: 50,
    quickSettingsPosition: 'right', quickSettingsAutoHide: false, showThemeToggle: true, showCleanModeToggle: true,
    bottomMenuVisible: true, bottomMenuRecommendVisible: true, bottomMenuRecommendMax: 5, bottomMenuRecentVisible: true, bottomMenuRecentMax: 5,
    showTopClock: true, showTopWeather: true,
    syncConflictPromptMode: 'always'
  };

  const [settings, setSettings] = useState(() => {
    if (backupData && backupData.data && backupData.data.settings) {
      return { ...defaultSettings, ...backupData.data.settings };
    }
    const saved = localStorage.getItem('tab365_settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch (e) {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  const isCleanMode = settings.layoutMode === 'clean';

  // Fetch preset default settings from server on initial visit if no local cached settings
  useEffect(() => {
    const saved = localStorage.getItem('tab365_settings');
    if (!saved && !(backupData && backupData.data && backupData.data.settings)) {
      fetch('/api/v1/preset/system-config')
        .then(res => res.json())
        .then(res => {
          if (res.code === 200 && res.data && typeof res.data === 'object' && Object.keys(res.data).length > 0) {
            const merged = { ...defaultSettings, ...res.data };
            // 根据初始 themeMode 选用对应的预设壁纸
            const initialMode = merged.themeMode || 'dark';
            if (initialMode === 'light' && merged.defaultLightWallpaper) {
              merged.wallpaper = merged.defaultLightWallpaper;
            } else if (initialMode === 'dark' && merged.defaultDarkWallpaper) {
              merged.wallpaper = merged.defaultDarkWallpaper;
            }
            setSettings(merged);
            localStorage.setItem('tab365_settings', JSON.stringify(merged));
          } else {
            localStorage.setItem('tab365_settings', JSON.stringify(defaultSettings));
          }
        })
        .catch(err => {
          console.warn('Failed to fetch system default settings, using local defaults', err);
          localStorage.setItem('tab365_settings', JSON.stringify(defaultSettings));
        });
    }
  }, []);

  // Persist updated settings to localStorage once cached
  useEffect(() => {
    const saved = localStorage.getItem('tab365_settings');
    if (saved) {
      localStorage.setItem('tab365_settings', JSON.stringify(settings));
    }
  }, [settings]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    let timeoutId = null;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 50);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
  const updateSetting = (key, val) => {
    if (key === 'themeMode') {
      const nextTheme = val;
      setSettings(s => {
        let nextWallpaper = s.wallpaper;
        // 如果切换为浅色主题，若配置了系统/自定义浅色壁纸则自动切换到浅色壁纸
        if (nextTheme === 'light') {
          if (s.defaultLightWallpaper) {
            nextWallpaper = s.defaultLightWallpaper;
          }
        } else {
          // 切换为深色主题
          if (s.defaultDarkWallpaper) {
            nextWallpaper = s.defaultDarkWallpaper;
          }
        }
        return { ...s, themeMode: nextTheme, wallpaper: nextWallpaper };
      });
      return;
    }
    setSettings(s => ({ ...s, [key]: val }));
  };

  const handleResetAllToDefault = async () => {
    try {
      // 1. Keep authentication token, remove all other local cached keys
      const token = localStorage.getItem('tab365_token');
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key !== 'tab365_token') {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      if (token) {
        localStorage.setItem('tab365_token', token);
      }

      // 2. Fetch system default settings from server
      let systemDefaults = null;
      try {
        const sysRes = await fetch('/api/v1/preset/system-config?t=' + Date.now());
        const sysJson = await sysRes.json();
        if (sysJson.code === 200 && sysJson.data && typeof sysJson.data === 'object') {
          systemDefaults = sysJson.data;
        }
      } catch (e) {
        console.warn('Failed to fetch system config during reset:', e);
      }

      // 3. Merge default settings and sync wallpaper according to themeMode
      const mergedSettings = { ...defaultSettings, ...(systemDefaults || {}) };
      const mode = mergedSettings.themeMode || 'dark';
      if (mode === 'light' && mergedSettings.defaultLightWallpaper) {
        mergedSettings.wallpaper = mergedSettings.defaultLightWallpaper;
      } else if (mode === 'dark' && mergedSettings.defaultDarkWallpaper) {
        mergedSettings.wallpaper = mergedSettings.defaultDarkWallpaper;
      }
      localStorage.setItem('tab365_settings', JSON.stringify(mergedSettings));
      setSettings(mergedSettings);

      // 4. Fetch preset shortcuts, menus and layouts
      await fetchPresets();

      // 5. Reset dockItems to server recommendShortcuts
      const rec = recommendShortcuts || [];
      localStorage.setItem('tab365_dock_items', JSON.stringify(rec));
      setDockItems(rec);
      setRecentClicks([]);
      setSearchHistory([]);

      // 6. Reset menuItems and pageData to server default
      if (defaultMenuItems && defaultMenuItems.length > 0) {
        setMenuItems(defaultMenuItems);
      }
      if (defaultPageData) {
        setPageData(defaultPageData);
      }

      // 7. If user is logged in, upload new default config to cloud
      if (token) {
        try {
          const serializedMenuItems = (defaultMenuItems || []).map(m => ({ 
            id: m.id, 
            title: m.title, 
            iconName: typeof m.icon === 'string' ? m.icon : undefined 
          }));
          const payload = {
            timestamp: Date.now(),
            data: {
              menuItems: serializedMenuItems,
              pageData: defaultPageData || {},
              settings: mergedSettings,
              searchHistory: []
            }
          };
          await fetch('/api/v1/sync/backup/upload', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
        } catch (e) {
          console.warn('Failed to upload default backup to cloud:', e);
        }
      }

      // 8. Reload window to ensure clean initial hydration
      window.location.reload();
      return true;
    } catch (err) {
      console.error('Failed to reset settings:', err);
      throw err;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      localStorage.setItem('tab365_token', token);
      setAuthToken(token);
      // Clean URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const handleMessage = (event) => {
      // Allow messages with specific type regardless of origin for easier local tunnel testing
      if (event.data && event.data.type === 'WECHAT_LOGIN_SUCCESS') {
        if (event.data.token) {
          localStorage.setItem('tab365_token', event.data.token);
          setAuthToken(event.data.token);
          setIsLoginModalOpen(false);
        } else {
          setIsLoginModalOpen(false);
          console.error("微信登录失败: Token为空，后端可能发生了异常");
          // Optionally notify user
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Fetch rolling hot searches from API
  useEffect(() => {
    const fetchHotSearches = () => {
      fetch('/api/v1/hotsearch/list?_t=' + Date.now())
        .then(res => res.json())
        .then(res => {
          if (res.code === 200 && res.data) {
            const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
            const baidu = (data['百度'] || []).slice(0, 5).map(item => ({ title: item.title, url: item.url }));
            const toutiao = (data['今日头条'] || []).slice(0, 5).map(item => ({ title: item.title, url: item.url }));
            const weibo = (data['微博'] || []).slice(0, 5).map(item => ({ title: item.title, url: item.url }));
            
            const combined = [...baidu, ...toutiao, ...weibo];
            const ranking = { baidu, toutiao, weibo };
            
            if (combined.length > 0) {
              setRollingHotSearches(combined);
              setHotSearchRanking(ranking);
            }
          }
        })
        .catch(console.error);
    };

    fetchHotSearches();
    const interval = setInterval(fetchHotSearches, 3600000); // Recheck every 1 hour
    return () => clearInterval(interval);
  }, []);

  // Roll hot search index every 8 seconds
  useEffect(() => {
    if (rollingHotSearches.length > 0) {
      const interval = setInterval(() => {
        setCurrentRollingIndex(prev => (prev + 1) % rollingHotSearches.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [rollingHotSearches]);

  // userInfo fetching is now handled by AuthContext

  useEffect(() => {
    if (isLoginModalOpen && loginTab === 'wechat') {
      if (!wechatConfig) {
        fetch('/api/v1/auth/wechat/config')
          .then(res => res.json())
          .then(data => {
            setWechatConfig(data);
          }).catch(console.error);
      }
      
      const loadWxScript = () => {
        const customCss = `
          body { background-color: transparent !important; padding: 0 !important; }
          .impowerBox { width: 100% !important; margin-top: -16px !important; }
          .impowerBox .qrcode { width: 270px !important; border: none !important; margin: 0 auto !important; }
          .impowerBox .title { display: none !important; }
          .impowerBox .info { width: 200px !important; }
          .status_icon { display: none !important; }
          .impowerBox .status { text-align: center !important; }
        `.replace(/\s+/g, ' ').trim();
        const wechatCss = "data:text/css;base64," + btoa(customCss);
        
        if (window.WxLogin && wechatConfig) {
          new window.WxLogin({
            self_redirect: true,
            id: "wechat-qrcode-container", 
            appid: wechatConfig.appId, 
            scope: "snsapi_login", 
            redirect_uri: wechatConfig.redirectUri,
            state: uid,
            style: "white",
            href: wechatCss
          });
        } else if (!window.WxLogin) {
          const script = document.createElement('script');
          script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
          script.async = true;
          script.onload = () => {
            if (wechatConfig) {
              new window.WxLogin({
                self_redirect: true,
                id: "wechat-qrcode-container", 
                appid: wechatConfig.appId, 
                scope: "snsapi_login", 
                redirect_uri: wechatConfig.redirectUri,
                state: uid,
                style: "white",
                href: wechatCss
              });
            }
          };
          document.body.appendChild(script);
        }
      };
      
      if (wechatConfig) {
        loadWxScript();
      }
    }
  }, [isLoginModalOpen, loginTab, wechatConfig, uid]);


  const AVAILABLE_ICON_NAMES = [
    'Home', 'Star', 'Heart', 'Globe', 'LayoutGrid', 'Search', 'Compass', 'Navigation', 'Bookmark', 'TrendingUp',
    'Code', 'Terminal', 'Cpu', 'Database', 'Briefcase', 'FolderOpen', 'FileText', 'ShoppingBag', 'ShoppingCart', 'CreditCard',
    'Film', 'Tv', 'Music', 'Headphones', 'Gamepad2', 'Camera', 'Image', 'Coffee', 'Flame', 'Sparkles',
    'BookOpen', 'GraduationCap', 'Languages', 'Newspaper', 'PenTool', 'Palette', 'Brush', 'Lightbulb', 'Calendar', 'Clock',
    'MessageCircle', 'MessageSquare', 'Mail', 'Users', 'User', 'Bell', 'Smartphone', 'Monitor', 'Laptop', 'Wifi',
    'CloudSun', 'Cloud', 'Package', 'Map', 'Car', 'Shield', 'Key', 'Settings', 'Calculator', 'Target'
  ];
  const AVAILABLE_ICONS = AVAILABLE_ICON_NAMES.map(name => IconMap[name] || Home);

  const ICON_LABELS = {
    Home: '主页', Star: '收藏', Heart: '喜爱', Globe: '网站', LayoutGrid: '应用', Search: '搜索', Compass: '发现', Navigation: '导航', Bookmark: '书签', TrendingUp: '热榜',
    Code: '开发', Terminal: '终端', Cpu: '技术', Database: '数据', Briefcase: '办公', FolderOpen: '资料', FileText: '文档', ShoppingBag: '购物', ShoppingCart: '商城', CreditCard: '支付',
    Film: '影视', Tv: '直播', Music: '音乐', Headphones: '音频', Gamepad2: '游戏', Camera: '摄影', Image: '图片', Coffee: '休闲', Flame: '热门', Sparkles: 'AI灵感',
    BookOpen: '阅读', GraduationCap: '学习', Languages: '翻译', Newspaper: '新闻', PenTool: '设计', Palette: '配色', Brush: '绘画', Lightbulb: '创意', Calendar: '日历', Clock: '时钟',
    MessageCircle: '聊天', MessageSquare: '社区', Mail: '邮箱', Users: '团队', User: '个人', Bell: '提醒', Smartphone: '移动端', Monitor: '桌面', Laptop: '数码', Wifi: '网络',
    CloudSun: '天气', Cloud: '云盘', Package: '快递', Map: '地图', Car: '出行', Shield: '安全', Key: '密码', Settings: '设置', Calculator: '计算器', Target: '目标'
  };

  const MAX_MENU_ITEMS = 8;
  const [maxScreenMenuItems, setMaxScreenMenuItems] = useState(8);

  useEffect(() => {
    const handleScreenResize = () => {
      const h = window.innerHeight;
      // Reserve top/bottom margins (60px) + bottom actions (add, divider, user avatar ~120px)
      // Compact menu items take ~42px each on smaller screens
      const availableHeight = h - 180;
      const count = Math.min(MAX_MENU_ITEMS, Math.max(3, Math.floor(availableHeight / 44)));
      setMaxScreenMenuItems(count);
    };
    handleScreenResize();
    window.addEventListener('resize', handleScreenResize);
    return () => window.removeEventListener('resize', handleScreenResize);
  }, []);

  const [menuItems, setMenuItems] = useState(() => {
    if (backupData && backupData.data && backupData.data.menuItems && backupData.data.menuItems.length > 0) {
      return backupData.data.menuItems.slice(0, MAX_MENU_ITEMS).map(m => {
        let iconName = m.iconName;
        if ((!iconName || iconName === 'Home') && defaultMenuItems) {
          const defaultItem = defaultMenuItems.find(d => d.id === m.id);
          if (defaultItem && typeof defaultItem.icon === 'string') {
            iconName = defaultItem.icon;
          }
        }
        return { ...m, iconName, icon: getIconByName(iconName) };
      });
    }
    if (defaultMenuItems && defaultMenuItems.length > 0) {
      return defaultMenuItems.slice(0, MAX_MENU_ITEMS);
    }
    // Fallback if network fails
    return [
      { id: 0, icon: Home, title: '主页' }
    ];
  });

  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, targetId: null, targetType: 'group', pageId: 0 });
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsActiveTab, setSettingsActiveTab] = useState('icons');
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [libraryModalSource, setLibraryModalSource] = useState('page'); // 'page' or 'dock'
  const [libraryActiveTab, setLibraryActiveTab] = useState('widget');
  const [isSettingsMaximized, setIsSettingsMaximized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [virtualPage, setVirtualPage] = useState(1);

  const TOTAL_PAGES = menuItems.length;

  const visibleMenuItems = useMemo(() => {
    if (menuItems.length <= maxScreenMenuItems) return menuItems;
    const currentIdx = menuItems.findIndex(m => m.id === currentPage);
    if (currentIdx === -1 || currentIdx < maxScreenMenuItems) {
      return menuItems.slice(0, maxScreenMenuItems);
    }
    const start = Math.max(0, Math.min(currentIdx, menuItems.length - maxScreenMenuItems));
    return menuItems.slice(start, start + maxScreenMenuItems);
  }, [menuItems, maxScreenMenuItems, currentPage]);

  useEffect(() => {
    if (isLoginModalOpen || isAddGroupModalOpen || isSettingsModalOpen || isLibraryModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLoginModalOpen, isAddGroupModalOpen, isSettingsModalOpen, isLibraryModalOpen]);

  const handleWheel = (e) => {
    if (isScrolling || activeWidgetPopup || isLoginModalOpen || isAddGroupModalOpen || isSettingsModalOpen || isLibraryModalOpen || deleteConfirmGroupId !== null) return;

    // Check if we should allow native scroll instead of page switch
    const pageSection = e.target.closest('.page-section');
    if (pageSection) {
      const isScrollable = pageSection.scrollHeight > pageSection.clientHeight;
      if (isScrollable) {
        const isAtTop = pageSection.scrollTop <= 0;
        const isAtBottom = Math.abs(pageSection.scrollHeight - pageSection.scrollTop - pageSection.clientHeight) <= 1;

        if (e.deltaY > 0 && !isAtBottom) {
          return; // Allow native scroll down
        }
        if (e.deltaY < 0 && !isAtTop) {
          return; // Allow native scroll up
        }
      }
    }
    
    if (e.deltaY > 20) {
      const currentIndex = menuItems.findIndex(m => m.id === currentPage);
      const nextIndex = (currentIndex + 1) % menuItems.length;
      const nextLogical = menuItems[nextIndex].id;
      const nextVirtual = virtualPage + 1;
      
      setIsTransitioning(true);
      setCurrentPage(nextLogical);
      setVirtualPage(nextVirtual);
      setIsScrolling(true);
      
      if (nextIndex === 0) {
        setTimeout(() => {
          setIsTransitioning(false);
          setVirtualPage(1);
          setIsScrolling(false);
        }, 600);
      } else {
        setTimeout(() => setIsScrolling(false), 400);
      }
    } else if (e.deltaY < -20) {
      const currentIndex = menuItems.findIndex(m => m.id === currentPage);
      const nextIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
      const nextLogical = menuItems[nextIndex].id;
      const nextVirtual = virtualPage - 1;
      
      setIsTransitioning(true);
      setCurrentPage(nextLogical);
      setVirtualPage(nextVirtual);
      setIsScrolling(true);
      
      if (nextIndex === menuItems.length - 1) {
        setTimeout(() => {
          setIsTransitioning(false);
          setVirtualPage(TOTAL_PAGES);
          setIsScrolling(false);
        }, 600);
      } else {
        setTimeout(() => setIsScrolling(false), 400);
      }
    }
  };



  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Reset scroll positions smoothly without causing jitters on the old page
  useEffect(() => {
    const pages = document.querySelectorAll('.page-section');
    if (pages[virtualPage]) {
      pages[virtualPage].scrollTop = 0;
    }
    
    // Sync the invisible clones for infinite looping
    const TOTAL_PAGES = menuItems.length;
    if (virtualPage === TOTAL_PAGES + 1 && pages[1]) pages[1].scrollTop = 0;
    if (virtualPage === 0 && pages[TOTAL_PAGES]) pages[TOTAL_PAGES].scrollTop = 0;

    // Wait until transition is done (600ms) before resetting other pages
    const timer = setTimeout(() => {
      pages.forEach((el, idx) => {
        if (idx !== virtualPage) el.scrollTop = 0;
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [virtualPage, menuItems.length]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    const d1 = date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
    const d2 = date.toLocaleDateString('zh-CN', { weekday: 'long' });
    return `${d1} ${d2}`;
  };

  const formatLunar = (date) => {
    const lunar = Lunar.fromDate(date);
    return `农历 ${lunar.getYearInGanZhi()}年 ${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}`;
  };

  const [activeEngine, setActiveEngine] = useState(defaultSearchEngines[0] || aiEngines[0]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [isSearchIconHovered, setIsSearchIconHovered] = useState(false);
  const [isRecommendDropdownOpen, setIsRecommendDropdownOpen] = useState(false);
  const [customSearchEngines, setCustomSearchEngines] = useState(() => {
    try {
      const saved = localStorage.getItem('tab365_customSearchEngines');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [selectedSearchEngineIds, setSelectedSearchEngineIds] = useState(() => {
    try {
      const saved = localStorage.getItem('tab365_selectedSearchEngines');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isSearchEngineModalOpen, setIsSearchEngineModalOpen] = useState(false);

  const allAvailableEngines = useMemo(() => {
    return [...searchEngines, ...customSearchEngines];
  }, [searchEngines, customSearchEngines]);

  const validSelectedIds = useMemo(() => {
    if (selectedSearchEngineIds.length === 0) {
      return allAvailableEngines.slice(0, 6).map(e => e.id);
    }
    const validIds = selectedSearchEngineIds.filter(id => allAvailableEngines.some(e => e.id === id));
    if (validIds.length === 0 && allAvailableEngines.length > 0) {
      return allAvailableEngines.slice(0, 6).map(e => e.id);
    }
    return validIds;
  }, [selectedSearchEngineIds, allAvailableEngines]);

  const displaySearchEngines = useMemo(() => {
    return allAvailableEngines.filter(e => validSelectedIds.includes(e.id));
  }, [allAvailableEngines, validSelectedIds]);
  const searchDropdownRef = useRef(null);
  const [isSearchHistoryOpen, setIsSearchHistoryOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      if (backupData && backupData.data && backupData.data.searchHistory) {
        return backupData.data.searchHistory;
      }
      const saved = localStorage.getItem('tab365_search_history');
      return saved ? JSON.parse(saved) : ['今天的天气'];
    } catch(e) { return ['今天的天气']; }
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setIsSearchDropdownOpen(false);
        setIsSearchEngineModalOpen(false);
        setIsSearchHistoryOpen(false);
        setIsRecommendDropdownOpen(false);
      }
    };
    if (isSearchDropdownOpen || isSearchEngineModalOpen || isSearchHistoryOpen || isRecommendDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchDropdownOpen, isSearchEngineModalOpen, isSearchHistoryOpen, isRecommendDropdownOpen]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      const val = e.target.value.trim();
      if (val !== '') {
        if (!searchHistory.includes(val)) {
          const newHistory = [val, ...searchHistory].slice(0, 10);
          setSearchHistory(newHistory);
          localStorage.setItem('tab365_search_history', JSON.stringify(newHistory));
        }
        setIsSearchHistoryOpen(false);
        const targetEngine = activeEngine || aiEngines[0];
        recordActionLog('perform_search', 'search_engine', targetEngine.id, { keyword: val, name: targetEngine.name }, targetEngine.name);
        window.open(generateSearchUrl(targetEngine, val), '_blank');
      } else if (rollingHotSearches.length > 0) {
        const targetSearch = rollingHotSearches[currentRollingIndex];
        if (targetSearch && targetSearch.url) {
          const rollingItemId = targetSearch.id || `rolling_${currentRollingIndex + 1}`;
          recordActionLog('click_hot_search', 'rolling_hot_search', rollingItemId, { url: targetSearch.url, name: targetSearch.title, platform: targetSearch.platform || 'rolling' }, targetSearch.title);
          window.open(targetSearch.url, '_blank');
        }
      }
    }
  };

  const [pageData, setPageData] = useState(() => {
      if (backupData && backupData.data && backupData.data.pageData) {
        const hydrated = {};
        const allPresets = [
          ...PRESET_GROUP_TYPES.flatMap(g => g.shortcuts),
          ...AVAILABLE_SHORTCUTS.flatMap(c => c.items),
          ...baseShortcuts,
          ...gameShortcuts,
          ...designShortcuts,
          ...devShortcuts,
          ...aiEngines
        ];
        if (defaultPageData) {
          Object.keys(defaultPageData).forEach(pageId => {
            hydrated[pageId] = defaultPageData[pageId] || [];
          });
        }
        Object.keys(backupData.data.pageData).forEach(pageId => {
          const rawItems = backupData.data.pageData[pageId];
          if (!rawItems || rawItems.length === 0) return;
          // 去除因旧版 Bug 导致的重复数据
          const deduplicated = rawItems.filter((item, index, self) => 
            index === self.findIndex((t) => t.id === item.id)
          );
          hydrated[pageId] = deduplicated.map(item => {
            if (item.type === 'widget') {
              let wDef = null;
              AVAILABLE_WIDGETS.forEach(cat => {
                const found = cat.items.find(w => w.widgetType === item.widgetType);
                if (found) wDef = found;
              });
              return wDef ? { ...wDef, id: item.id, w: item.w, h: item.h, type: 'widget' } : item;
            } else if (item.type === 'preset_shortcut') {
              const pDef = allPresets.find(s => s.url === item.url);
              return pDef ? { ...pDef, id: item.id, type: 'shortcut' } : { ...item, type: 'shortcut' };
            } else {
              const pDef = allPresets.find(s => s?.url === item.url);
              let resolvedIcon = item.icon || pDef?.icon;
              if (typeof resolvedIcon === "string") resolvedIcon = resolvedIcon.replace("www.365tab.com", "www.365tab.link");
              if (resolvedIcon && typeof resolvedIcon === 'object') {
                resolvedIcon = item.iconName ? getIconByName(item.iconName) : (pDef?.iconName ? getIconByName(pDef.iconName) : getIconByName('Globe'));
              }
              return pDef ? { ...item, icon: resolvedIcon, type: 'shortcut' } : { ...item, icon: resolvedIcon, type: 'shortcut' };
            }
          });
        });
        return hydrated;
      }
      
      if (Object.keys(defaultPageData).length > 0) {
          return defaultPageData;
      }
      
      return {
        0: [{ id: 'w_0', type: 'widget', widgetType: 'weather', w: 2, h: 2 }]
      };
  });

  // 拉取全局搜索引擎配置
  useEffect(() => {
    fetch('/api/v1/preset/sync')
      .then(res => res.json())
      .then(res => {
        if (res.code === 200 && res.data) {
          const dict = res.data;
          const dynamicEngines = Object.keys(dict)
            .filter(key => key.startsWith('SEARCH_ENGINE:'))
            .map(key => JSON.parse(dict[key]))
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          if (dynamicEngines.length > 0) {
            setSearchEngines(dynamicEngines);
            setActiveEngine(prev => {
                if (prev) {
                    const freshEngine = dynamicEngines.find(e => e.id === prev.id);
                    if (freshEngine) return freshEngine;
                }
                return dynamicEngines[0];
            });
          }

          // 若当前未配置壁纸，则根据当前主题模式（深色/浅色）优先取后台系统配置壁纸，或从“默认”分类中选择
          setSettings(prev => {
            if (prev && prev.wallpaper) return prev;
            const currentMode = prev?.themeMode || 'dark';
            let targetWallpaper = currentMode === 'light' ? prev?.defaultLightWallpaper : prev?.defaultDarkWallpaper;
            if (targetWallpaper) {
              const updated = { ...prev, wallpaper: targetWallpaper };
              try { localStorage.setItem('tab365_settings', JSON.stringify(updated)); } catch (e) {}
              return updated;
            }
            const wps = Object.keys(dict)
              .filter(k => k.startsWith('WALLPAPER:'))
              .map(k => {
                try { return JSON.parse(dict[k]); } catch (e) { return null; }
              })
              .filter(Boolean)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            if (wps.length === 0) return prev;
            const defaultWp = wps.find(w => w.category === '默认' || w.category === 'default') || wps[0];
            if (defaultWp && defaultWp.src) {
              const updated = { ...prev, wallpaper: defaultWp.src };
              try { localStorage.setItem('tab365_settings', JSON.stringify(updated)); } catch (e) {}
              return updated;
            }
            return prev;
          });
        }
      })
      .catch(err => console.error('Failed to fetch search engines', err));
  }, []);

  // 漫游逻辑：登录成功后拉取最新云端配置覆盖本地
  useEffect(() => {
    if (authToken && userInfo) {
      fetch('/api/v1/sync/backup/latest', {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
      .then(res => res.json())
      .then(res => {
        if (res.code === 200 && res.data && res.data.data) {
          try {
            const parsedData = typeof res.data.data === 'string' ? JSON.parse(res.data.data) : res.data.data;
            const cloudTimestamp = parsedData.timestamp || 0;
            let localTimestamp = 0;
            
            const localDataStr = localStorage.getItem(`tab365_visitor_backup_${uid}`);
            if (localDataStr) {
              try {
                const localData = JSON.parse(localDataStr);
                localTimestamp = localData.timestamp || 0;
              } catch(e){}
            }
            
            const applyCloudData = () => {
              const actualData = parsedData.data || {};
              if (actualData.menuItems) {
                setMenuItems(actualData.menuItems.map(m => ({ ...m, icon: getIconByName(m.iconName) })));
              }
              if (actualData.pageData) {
                const hydrated = {};
                const allPresetsToHydrate = [
                  ...PRESET_GROUP_TYPES.flatMap(g => g.shortcuts),
                  ...AVAILABLE_SHORTCUTS.flatMap(c => c.items),
                  ...baseShortcuts,
                  ...gameShortcuts,
                  ...designShortcuts,
                  ...devShortcuts,
                  ...aiEngines
                ];
                Object.keys(actualData.pageData).forEach(pageId => {
                  const rawItems = actualData.pageData[pageId];
                  const deduplicated = rawItems.filter((item, index, self) => 
                    index === self.findIndex((t) => t.id === item.id)
                  );
                  hydrated[pageId] = deduplicated.map(item => {
                    if (item.type === 'widget') {
                      let wDef = null;
                      AVAILABLE_WIDGETS.forEach(cat => {
                        const found = cat.items.find(w => w.widgetType === item.widgetType);
                        if (found) wDef = found;
                      });
                      return wDef ? { ...wDef, id: item.id, w: item.w, h: item.h, type: 'widget' } : item;
                    } else if (item.type === 'preset_shortcut') {
                      const pDef = allPresetsToHydrate.find(s => s.url === item.url);
                      return pDef ? { ...pDef, id: item.id, type: 'shortcut' } : { ...item, type: 'shortcut' };
                    } else {
                      const pDef = allPresetsToHydrate.find(s => s?.url === item.url);
                      let resolvedIcon = item.icon || pDef?.icon;
                      if (resolvedIcon && typeof resolvedIcon === 'object') {
                        resolvedIcon = item.iconName ? getIconByName(item.iconName) : (pDef?.iconName ? getIconByName(pDef.iconName) : getIconByName('Globe'));
                      }
                      return pDef ? { ...item, icon: resolvedIcon, type: 'shortcut' } : { ...item, icon: resolvedIcon, type: 'shortcut' };
                    }
                  });
                });
                setPageData(hydrated);
              }
              if (actualData.settings) {
                setSettings(actualData.settings);
              }
              if (actualData.searchHistory) {
                setSearchHistory(actualData.searchHistory);
              }
              localStorage.setItem(`tab365_visitor_backup_${uid}`, JSON.stringify(parsedData));
            };
            
            const isConflict = localDataStr && localTimestamp > 0 && cloudTimestamp > 0 && localTimestamp !== cloudTimestamp;
            const mode = settings.syncConflictPromptMode || 'always';
            const hasChecked = sessionStorage.getItem('has_checked_sync_conflict');
            
            if (isConflict) {
              if (mode === 'disabled') {
                applyCloudData();
              } else if (mode === 'on_login' && hasChecked) {
                applyCloudData();
              } else {
                setSyncConflict({
                  cloudData: parsedData,
                  cloudTimestamp,
                  localTimestamp,
                  applyCloudData
                });
                sessionStorage.setItem('has_checked_sync_conflict', '1');
              }
            } else {
              applyCloudData();
            }
          } catch (e) {
            console.error('解析云端配置失败', e);
          }
        } else if (res.code === 404) {
          // 云端无配置，直接将本地配置同步至云端
          const localDataStr = localStorage.getItem(`tab365_visitor_backup_${uid}`);
          if (localDataStr) {
            fetch('/api/v1/sync/backup/upload', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
              body: localDataStr
            }).catch(err => console.error('首次登录上传本地配置失败', err));
          }
        }
      })
      .catch(err => console.error('拉取云端配置失败', err));
    }
  }, [authToken, userInfo]);

  useAutoBackup(uid, menuItems, pageData, settings, searchHistory);

  const handleImageError = (e, name) => {
    if (e.target.getAttribute('data-error-handled')) return;
    e.target.setAttribute('data-error-handled', 'true');
    e.target.style.display = 'none';
    const parent = e.target.parentElement;
    if (parent) {
        const fallback = document.createElement('div');
        fallback.style.width = '100%';
        fallback.style.height = '100%';
        fallback.style.display = 'flex';
        fallback.style.alignItems = 'center';
        fallback.style.justifyContent = 'center';
        fallback.style.backgroundColor = 'rgba(255,255,255,0.2)';
        fallback.style.borderRadius = 'inherit';
        fallback.style.color = parent.className.includes('dock') ? '#333' : '#fff';
        fallback.style.fontSize = '1.5rem';
        fallback.style.fontWeight = 'bold';
        fallback.innerText = name ? name.charAt(0) : '?';
        parent.style.overflow = 'hidden';
        parent.appendChild(fallback);
    }
  };

  const [draggingId, setDraggingId] = useState(null);

  const handleDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, pageId, targetId) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;

    setPageData((prev) => {
      const pageItems = prev[pageId];
      if (!pageItems) return prev;

      const draggedIndex = pageItems.findIndex(i => i.id === draggingId);
      const targetIndex = pageItems.findIndex(i => i.id === targetId);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const newItems = [...pageItems];
      const draggedItem = newItems.splice(draggedIndex, 1)[0];
      newItems.splice(targetIndex, 0, draggedItem);
      
      return { ...prev, [pageId]: newItems };
    });
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };



  const closeContextMenu = () => {
    if (contextMenu.visible) {
      setContextMenu({ ...contextMenu, visible: false });
    }
  };

  const closeModalsOnOutsideClick = () => {
    closeContextMenu();
    if (addMenuOpenId !== null) {
      setAddMenuOpenId(null);
    }
  };

  const currentFontSize = Math.max(12, Math.round(settings.iconSize * 0.18));
  // 1.7 * fontSize accounts for margin-top (0.4em) and line-height (1.3)
  const textTotalHeight = settings.iconNameVisible ? (currentFontSize * 1.7) : 0;
  
  // Guarantee perfect square footprint while preventing text overlap.
  // Multiplied by 1.5 for a more breathable, premium desktop layout.
  const actualGap = Math.max(settings.iconDistance * 1.5, textTotalHeight + 6);
  
  const gridItemWidth = settings.iconSize;
  const gridItemHeight = settings.iconSize;
  const rowGap = actualGap;
  const columnGap = actualGap;
  const widget2x2Height = 2 * settings.iconSize + actualGap;
  const widgetScale = settings.iconSize / 72;

  // Calculate dynamic zoom to ensure 12 columns fit on screen
  const requiredGridWidth = 12 * gridItemWidth + 11 * columnGap;
  const sidebarWidth = (settings.sidebarPosition !== 'hidden' && !settings.sidebarAutoHide) ? settings.sidebarWidth : 0;
  // available width in main-content (max-width: 95vw, minus some padding/sidebar)
  const maxAvailableWidth = (windowWidth - sidebarWidth) * 0.95 - 40; 
  
  let dynamicZoom = 1;
  let modalZoom = 1;
  const h = window.innerHeight;
  if (windowWidth <= 1024 || h <= 700) {
    dynamicZoom = 0.75;
    modalZoom = 0.65;
  } else if (windowWidth <= 1400 || h <= 850) {
    dynamicZoom = 0.85;
    modalZoom = 0.78;
  }

  if (requiredGridWidth * dynamicZoom > maxAvailableWidth && maxAvailableWidth > 0) {
    const fitZoom = maxAvailableWidth / requiredGridWidth;
    dynamicZoom = Math.min(dynamicZoom, fitZoom);
  }
  
  modalZoom = Math.min(modalZoom, dynamicZoom);

  const renderAddTile = (pageId) => (
    <motion.div
      custom={pageData[pageId] ? pageData[pageId].length : 0}
      variants={itemVariants}
      initial="hidden"
      animate={currentPage === pageId ? "visible" : "hidden"}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="shortcut-item add-new-item"
      style={{
        cursor: 'pointer',
        position: 'relative'
      }}
      onClick={(e) => {
        e.stopPropagation();
        setLibraryModalSource('page');
        setLibraryActiveTab('shortcut');
        setIsLibraryModalOpen(true);
      }}
    >
      <div 
        className="shortcut-icon"
        style={{
          width: `${settings.iconSize}px`,
          height: `${settings.iconSize}px`,
          borderRadius: settings.iconStyle === 'circle' ? '50%' : `${settings.iconRadius / 2}%`,
          background: 'var(--bg-hover-light, rgba(255,255,255,0.05))',
          border: '2px dashed rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted, rgba(255,255,255,0.5))',
          transition: 'all 0.2s',
          boxSizing: 'border-box',
          flexShrink: 0
        }}
        onMouseEnter={(e) => { 
          e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; 
          e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; 
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
        }}
        onMouseLeave={(e) => { 
          e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; 
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
        }}
      >
        <Plus size={settings.iconSize * 0.4} />
      </div>
      {settings.iconNameVisible !== false && (
        <span className="shortcut-name" style={{
          marginTop: '0.5em',
          fontSize: `${currentFontSize}px`,
          ...(settings.iconColor === 'rainbow' 
            ? { backgroundImage: 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
            : { color: settings.iconColor })
        }}>
          添加
        </span>
      )}
      
    </motion.div>
  );

  const renderTopClock = () => (
    <div 
      key="clock"
      className="clock-container"
    >
      <div className={`hover-widget clock-hover-area ${settings.showTopClock === false ? 'is-closed' : ''}`}
           onClick={settings.showTopClock === false ? () => updateSetting('showTopClock', true) : undefined}>
        <div className="widget-content" style={{ opacity: settings.showTopClock === false ? 0 : 1, pointerEvents: settings.showTopClock === false ? 'none' : 'auto' }}>
          <div className="widget-close-btn" onClick={(e) => { e.stopPropagation(); updateSetting('showTopClock', false); }} title="隐藏时钟">
            <X size={16} />
          </div>
          <div className="flip-clock">
            {formatTime(time).split('').map((char, index) => (
              char === ':' ? <span key={index} className="colon">:</span> :
              <FlipDigit key={index} char={char} />
            ))}
          </div>
          <div className="date-group">
            <p className="date">{formatDate(time)}</p>
            <p className="lunar-date">{formatLunar(time)}</p>
          </div>
        </div>
        {settings.showTopClock === false && (
          <div className="placeholder-overlay">
            <Plus size={24} />
            <span>点击添加日期/时钟</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderTopWeather = () => {
    if (!globalWeatherData) {
      return (
        <div key="weather" className="weather-container">
          <div className={`hover-widget weather-hover-area ${settings.showTopWeather === false ? 'is-closed' : ''}`}
               onClick={settings.showTopWeather === false ? () => updateSetting('showTopWeather', true) : undefined}>
            <div className="widget-content" style={{ opacity: settings.showTopWeather === false ? 0 : 1, pointerEvents: settings.showTopWeather === false ? 'none' : 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '140px' }}>
              <div className="widget-close-btn" onClick={(e) => { e.stopPropagation(); updateSetting('showTopWeather', false); }} title="隐藏天气">
                <X size={16} />
              </div>
              <Loader className="spin" size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
            {settings.showTopWeather === false && (
              <div className="placeholder-overlay">
                <Plus size={24} />
                <span>点击添加天气预报</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    const temp = globalWeatherData.now?.temp;
    const text = globalWeatherData.now?.text;
    const locationName = globalWeatherData.location?.name;
    const locationCity = globalWeatherData.location?.adm2;
    const dailyForecasts = globalWeatherData.daily || [];
    const today = dailyForecasts[0] || {};
    const airCategory = globalWeatherData.air?.category;

    return (
    <div 
      key="weather"
      className="weather-container"
    >
      <div className={`hover-widget weather-hover-area ${settings.showTopWeather === false ? 'is-closed' : ''}`}
           onClick={settings.showTopWeather === false ? () => updateSetting('showTopWeather', true) : undefined}>
        <div className="widget-content" style={{ opacity: settings.showTopWeather === false ? 0 : 1, pointerEvents: settings.showTopWeather === false ? 'none' : 'auto' }}>
          <div className="widget-close-btn" onClick={(e) => { e.stopPropagation(); updateSetting('showTopWeather', false); }} title="隐藏天气">
            <X size={16} />
          </div>
          <div className="weather-header">
            <CloudSun size={42} className="weather-icon" />
            <h1 className="temp">{temp}°C</h1>
          </div>
          <div className="weather-group">
            <p className="desc" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span>{text}</span>
              {today.tempMin && today.tempMax && <span>{today.tempMin}°~{today.tempMax}°</span>}
              {airCategory && <span style={{ padding: '2px 6px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', fontSize: '0.8rem' }}>空气 {airCategory}</span>}
            </p>
            <p className="location">{locationCity} {locationName}</p>
          </div>
        </div>
        {settings.showTopWeather === false && (
          <div className="placeholder-overlay">
            <Plus size={24} />
            <span>点击添加天气预报</span>
          </div>
        )}
      </div>
    </div>
  );
};

  return (
    <div 
      className={`app-container theme-${settings.themeMode || 'dark'} ${settings.iconBackgroundVisible === false ? 'no-glass-icons' : ''}`} 
      style={{ '--app-zoom': dynamicZoom, '--modal-zoom': modalZoom }}
      onWheel={handleWheel}
      onContextMenu={(e) => { e.preventDefault(); closeContextMenu(); }}
      onClick={closeModalsOnOutsideClick}
    >
      {isVideoUrl(settings.wallpaper) ? (
        <video
          key={settings.wallpaper}
          className={`background-video-layer ${isCleanMode ? 'clean-mode' : ''}`}
          src={settings.wallpaper}
          autoPlay
          loop
          muted
          playsInline
          style={{
            ...(settings.wallpaperBlur > 0 ? { filter: `brightness(0.7) contrast(1.1) blur(${settings.wallpaperBlur / 4}px)` } : {})
          }}
        />
      ) : (
        <div 
          className={`background-layer ${isCleanMode ? 'clean-mode' : ''}`}
          style={{
            ...(settings.wallpaper ? { '--bg-image': `url('${settings.wallpaper}')` } : {}),
            ...(settings.wallpaperBlur > 0 ? { '--bg-blur': `blur(${settings.wallpaperBlur / 4}px)` } : {})
          }}
        ></div>
      )}
      <div 
        className="background-mask" 
        style={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1, 
          backgroundColor: `rgba(0, 0, 0, ${(settings.wallpaperOpacity || 0) / 100})`,
          pointerEvents: 'none' 
        }}
      ></div>
      
      {!isCleanMode && (
        <div 
          className={`sidebar-menu-bar ${settings.sidebarPosition} ${settings.sidebarAutoHide ? 'auto-hide' : ''}`}
          style={{
            background: settings.themeMode === 'light' ? `rgba(0, 0, 0, ${settings.sidebarOpacity * 0.85 / 100})` : `rgba(255, 255, 255, ${settings.sidebarOpacity / 100})`,
            width: `${settings.sidebarWidth}px`
          }}
        >
          <div className="sidebar-menu-list hover-scrollbar">
            {visibleMenuItems.map((item) => {
              const Icon = (typeof item.icon === 'string' ? getIconByName(item.icon) : item.icon) || Home;
              const isActive = currentPage === item.id;
              return (
                <div 
                  key={item.id}
                  className={`menu-item ${isActive ? 'active' : ''}`}
                  title={item.title}
                  onClick={() => {
                    const targetIdx = menuItems.findIndex(m => m.id === item.id);
                    setIsTransitioning(true);
                    setCurrentPage(item.id);
                    setVirtualPage(targetIdx + 1);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetId: item.id, targetType: 'group', pageId: item.id });
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <Icon size={26} />
                    {isDeleteGroupMode && item.id !== 0 && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-8px',
                          background: '#ff4d4f',
                          borderRadius: '50%',
                          width: '14px',
                          height: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          zIndex: 10
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmGroupId(item.id);
                        }}
                      >
                        <Minus size={12} strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: 'inherit', whiteSpace: 'nowrap', maxWidth: '64px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.title}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sidebar-bottom-actions">
            <div 
              className="menu-item" 
              title={menuItems.length >= MAX_MENU_ITEMS ? "已达上限(最多8个)，点击可删除分组" : "新增分组"}
              onClick={(e) => {
                e.stopPropagation(); // prevent app-container onClick from immediately closing something if needed
                if (menuItems.length >= MAX_MENU_ITEMS) {
                  setIsDeleteGroupMode(!isDeleteGroupMode);
                  return;
                }
                if (isAddGroupModalOpen && editingMenuId === null) {
                  setIsAddGroupModalOpen(false);
                } else {
                  setEditingMenuId(null);
                  setNewGroupName('');
                  setSelectedIconIndex(0);
                  setAddGroupTab('preset');
                  setIsAddGroupModalOpen(true);
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {menuItems.length >= MAX_MENU_ITEMS ? <Minus size={26} color={isDeleteGroupMode ? '#ff4d4f' : undefined} /> : <Plus size={26} />}
              </div>
              <div style={{ fontSize: '12px', color: isDeleteGroupMode && menuItems.length >= MAX_MENU_ITEMS ? '#ff4d4f' : 'inherit' }}>
                {menuItems.length >= MAX_MENU_ITEMS ? "删除" : "添加"}
              </div>
            </div>
            
            <div className="menu-divider" style={{ width: '60%', height: '1px', background: settings.themeMode === 'light' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)', margin: '0.2rem auto' }}></div>
            
            <div 
              className="menu-item login-menu-btn" 
              title={userInfo ? `${userInfo.nickname || '用户'} (点击打开设置)` : "登录"}
              onClick={() => {
                if (userInfo) {
                  setSettingsActiveTab('account');
                  setIsSettingsModalOpen(true);
                } else {
                  setIsLoginModalOpen(true);
                }
              }}
              style={{ padding: '6px 4px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {userInfo ? (
                  <img src={userInfo.avatar || '/chicken.png'} alt="avatar" style={{ 
                    width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover',
                    filter: userInfo.avatar ? 'none' : 'brightness(0) invert(1)' 
                  }} />
                ) : (
                  <User size={26} />
                )}
              </div>
              {!userInfo && (
                <div style={{ fontSize: '12px', color: 'inherit', marginTop: '4px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                  登录
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`bottom-right-actions ${settings.quickSettingsPosition === 'left' ? 'pos-left' : 'pos-right'} ${settings.quickSettingsAutoHide ? 'auto-hide' : ''}`}>
        {!isQuickSettingsCollapsed && (
          <>
            <div 
              className="action-btn" 
              style={{ position: 'relative' }} 
              data-title="消息中心" 
              onClick={(e) => { e.stopPropagation(); setIsMessageCenterOpen(!isMessageCenterOpen); setViewingMessage(null); setMsgPage(1); }}
            >
              <Mail size={22} />
              {unreadCount > 0 && (
                <div style={{ position: 'absolute', top: '4px', right: '4px', width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%' }}></div>
              )}
              
              <AnimatePresence>
                {isMessageCenterOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      right: 0,
                      marginBottom: '12px',
                      width: '320px',
                      background: settings.themeMode === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '16px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                      border: settings.themeMode === 'light' ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'default',
                      zIndex: 1000
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    {viewingMessage ? (
                      // Detail View
                      <div style={{ display: 'flex', flexDirection: 'column', height: '400px' }}>
                        <div style={{ padding: '16px', borderBottom: settings.themeMode === 'light' ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ cursor: 'pointer', padding: '4px', borderRadius: '50%', background: settings.themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)' }} onClick={() => setViewingMessage(null)}>
                            <ArrowLeft size={18} color={settings.themeMode === 'light' ? '#333' : '#fff'} />
                          </div>
                          <div style={{ fontWeight: 'bold', color: settings.themeMode === 'light' ? '#333' : '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {viewingMessage.type === 'SYSTEM_LOG' ? '📢 系统公告' : '💬 私信通知'}
                          </div>
                          <div 
                            style={{ cursor: 'pointer', padding: '4px', borderRadius: '50%', background: settings.themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)' }} 
                            onClick={(e) => { e.stopPropagation(); setIsMessageCenterOpen(false); }}
                          >
                            <X size={18} color={settings.themeMode === 'light' ? '#333' : '#fff'} />
                          </div>
                        </div>
                        <div className="hover-scrollbar" style={{ padding: '16px', overflowY: 'auto', flex: 1, color: settings.themeMode === 'light' ? '#333' : '#fff', fontSize: '0.95rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                          {viewingMessage.content}
                        </div>
                        <div style={{ padding: '12px 16px', fontSize: '0.8rem', color: settings.themeMode === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', borderTop: settings.themeMode === 'light' ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', textAlign: 'right' }}>
                          {new Date(viewingMessage.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      // List View
                      <div style={{ display: 'flex', flexDirection: 'column', height: '400px' }}>
                        <div style={{ padding: '16px', borderBottom: settings.themeMode === 'light' ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: settings.themeMode === 'light' ? '#333' : '#fff' }}>
                            消息中心
                          </div>
                          <div 
                            style={{ cursor: 'pointer', padding: '4px', borderRadius: '50%', background: settings.themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)' }} 
                            onClick={(e) => { e.stopPropagation(); setIsMessageCenterOpen(false); }}
                          >
                            <X size={18} color={settings.themeMode === 'light' ? '#333' : '#fff'} />
                          </div>
                        </div>
                        <div className="hover-scrollbar" style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
                          {messages.length === 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: settings.themeMode === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)' }}>
                              暂无消息
                            </div>
                          ) : (
                            messages.slice((msgPage - 1) * 10, msgPage * 10).map(msg => (
                              <div 
                                key={msg.id}
                                onClick={() => {
                                  handleMessageRead(msg);
                                  setViewingMessage(msg);
                                }}
                                style={{
                                  padding: '12px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  marginBottom: '4px',
                                  background: msg.isRead === 1 ? 'transparent' : (settings.themeMode === 'light' ? 'rgba(59,130,246,0.05)' : 'rgba(59,130,246,0.15)'),
                                  display: 'flex',
                                  gap: '12px',
                                  alignItems: 'flex-start',
                                  textAlign: 'left'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = settings.themeMode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)'}
                                onMouseLeave={e => e.currentTarget.style.background = msg.isRead === 1 ? 'transparent' : (settings.themeMode === 'light' ? 'rgba(59,130,246,0.05)' : 'rgba(59,130,246,0.15)')}
                              >
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: msg.isRead === 1 ? 'transparent' : '#3b82f6', marginTop: '6px', flexShrink: 0 }}></div>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <div style={{ color: settings.themeMode === 'light' ? '#333' : '#fff', fontSize: '0.95rem', fontWeight: msg.isRead === 1 ? 'normal' : '500', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {msg.type === 'SYSTEM_LOG' ? '📢 系统公告' : '💬 私信通知'}
                                  </div>
                                  <div style={{ color: settings.themeMode === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {msg.content}
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        {/* Pagination */}
                        {messages.length > 10 && (
                          <div style={{ padding: '12px', borderTop: settings.themeMode === 'light' ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button 
                              style={{ padding: '4px 12px', borderRadius: '12px', border: 'none', background: settings.themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)', color: settings.themeMode === 'light' ? '#333' : '#fff', cursor: msgPage === 1 ? 'not-allowed' : 'pointer', opacity: msgPage === 1 ? 0.5 : 1 }}
                              disabled={msgPage === 1}
                              onClick={() => setMsgPage(p => p - 1)}
                            >
                              上一页
                            </button>
                            <span style={{ fontSize: '0.8rem', color: settings.themeMode === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)' }}>
                              {msgPage} / {Math.ceil(messages.length / 10)}
                            </span>
                            <button 
                              style={{ padding: '4px 12px', borderRadius: '12px', border: 'none', background: settings.themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)', color: settings.themeMode === 'light' ? '#333' : '#fff', cursor: msgPage === Math.ceil(messages.length / 10) ? 'not-allowed' : 'pointer', opacity: msgPage === Math.ceil(messages.length / 10) ? 0.5 : 1 }}
                              disabled={msgPage === Math.ceil(messages.length / 10)}
                              onClick={() => setMsgPage(p => p + 1)}
                            >
                              下一页
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="action-divider"></div>
    
            {settings.showCleanModeToggle !== false && (
              <>
                <div 
                  className="action-btn" 
                  style={{ 
                    color: isCleanMode ? "#4ade80" : undefined,
                    background: isCleanMode ? 'rgba(74, 222, 128, 0.2)' : '' 
                  }} 
                  data-title="一键绿色" 
                  onClick={() => updateSetting('layoutMode', isCleanMode ? 'widget' : 'clean')}
                >
                  <Leaf size={22} />
                </div>
                <div className="action-divider"></div>
              </>
            )}
            {settings.showThemeToggle !== false && (
              <>
                <div 
                  className="action-btn" 
                  data-title={settings.themeMode === 'light' ? '切换深色主题' : '切换浅色主题'} 
                  onClick={() => updateSetting('themeMode', settings.themeMode === 'light' ? 'dark' : 'light')}
                >
                  <Palette size={22} />
                </div>
                <div className="action-divider"></div>
              </>
            )}
    
            <div className="action-btn" data-title="设置" onClick={(e) => { e.stopPropagation(); setIsSettingsModalOpen(true); }}>
              <Settings size={22} />
            </div>
          </>
        )}
        
        <div className="action-divider" style={{ display: isQuickSettingsCollapsed ? 'none' : 'block' }}></div>
        <div 
          className="action-btn" 
          style={{ 
            background: settings.themeMode === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)'
          }}
          data-title={isQuickSettingsCollapsed ? "展开" : "收起"} 
          onClick={(e) => { e.stopPropagation(); setIsQuickSettingsCollapsed(!isQuickSettingsCollapsed); }}
        >
          {isQuickSettingsCollapsed ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
        </div>
      </div>

      <WidgetModal isOpen={!!activeWidgetPopup && activeWidgetPopup !== 'hotsearch'} onClose={() => setActiveWidgetPopup(null)}>
        {activeWidgetPopup === 'weather' && <WeatherModal globalWeatherData={globalWeatherData} />}
      </WidgetModal>
      
      <HotSearchModal isOpen={activeWidgetPopup === 'hotsearch'} onClose={() => setActiveWidgetPopup(null)} />


      <main className="main-content" style={{
        '--grid-row-gap': `${rowGap}px`,
        '--grid-column-gap': `${columnGap}px`,
        '--grid-item-width': `${gridItemWidth}px`,
        '--grid-item-height': `${gridItemHeight}px`,
        '--widget-2x2-height': `${widget2x2Height}px`
      }}>
        <div className={`top-row ${isCleanMode ? 'clean-mode' : ''}`}>
          {!isCleanMode && renderTopClock()}

          <div className="center-container" style={{ position: 'relative', zIndex: 100 }}>
            {isCleanMode && (
              <div className="weather-container horizontal">
                {!globalWeatherData ? (
                  <Loader className="spin" size={24} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <>
                    <CloudSun size={32} className="weather-icon" />
                    <h1 className="temp">{globalWeatherData.now?.temp}°C</h1>
                    <p className="desc" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span>{globalWeatherData.now?.text}</span>
                      {globalWeatherData.daily?.[0] && <span>{globalWeatherData.daily[0].tempMin}°~{globalWeatherData.daily[0].tempMax}°</span>}
                      {globalWeatherData.air?.category && <span style={{ padding: '2px 6px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', fontSize: '0.8rem' }}>空气 {globalWeatherData.air.category}</span>}
                    </p>
                    <p className="location">{globalWeatherData.location?.adm2} {globalWeatherData.location?.name}</p>
                  </>
                )}
              </div>
            )}
            <div className="search-container" ref={searchDropdownRef}>
              <div className="search-box-wrapper">
                <div 
                  className="search-engine-selector" 
                  onMouseEnter={() => setIsSearchIconHovered(true)}
                  onMouseLeave={() => setIsSearchIconHovered(false)}
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    const nextState = !isSearchDropdownOpen;
                    setIsSearchDropdownOpen(nextState); 
                    if (nextState) { setIsSearchHistoryOpen(false); setIsRecommendDropdownOpen(false); }
                    setIsSearchEngineModalOpen(false); 
                  }}
                  style={{
                    background: isSearchDropdownOpen || isSearchIconHovered ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    backdropFilter: isSearchDropdownOpen || isSearchIconHovered ? 'blur(10px)' : 'none',
                    padding: '4px 8px',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '34px',
                    height: '34px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', flexShrink: 0 }}>
                    {activeEngine?.icon ? (
                      typeof activeEngine.icon === 'string' ? (
                        <img src={activeEngine.icon} alt="icon" style={{ width: 22, height: 22, borderRadius: '50%' }} />
                      ) : (
                        <activeEngine.icon size={22} color="rgba(255,255,255,0.8)" />
                      )
                    ) : (
                      <Search className="search-icon" size={22} />
                    )}
                  </div>
                  <div style={{
                    width: isSearchIconHovered || isSearchDropdownOpen ? '18px' : '0px',
                    opacity: isSearchIconHovered || isSearchDropdownOpen ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}>
                    <ChevronDown size={14} style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', transition: 'transform 0.2s', transform: isSearchDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                  </div>
                </div>
                <AnimatePresence>
                  {isSearchDropdownOpen && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, y: -10 }} 
                      className="search-engine-dropdown"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <div className="search-engine-grid">
                        {[...displaySearchEngines, ...customSearchEngines].map((engine) => (
                          <div 
                            key={engine.id} 
                            className={`search-engine-item ${activeEngine?.id === engine.id ? 'active' : ''}`} 
                            title={engine.name}
                            onClick={() => { setActiveEngine(engine); setIsSearchDropdownOpen(false); }}
                            style={{
                              backgroundColor: activeEngine?.id === engine.id ? (settings.themeMode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255, 255, 255, 0.2)') : 'transparent',
                              border: activeEngine?.id === engine.id ? (settings.themeMode === 'light' ? '1px solid rgba(0,0,0,0.2)' : '1px solid rgba(255, 255, 255, 0.5)') : '1px solid transparent',
                              borderRadius: '12px',
                              color: settings.themeMode === 'light' ? '#333' : '#fff'
                            }}
                          >
                            <div className="search-engine-delete-btn" onClick={(e) => { 
                              e.stopPropagation(); 
                              // Remove from custom engines if it's there
                              if (customSearchEngines.find(c => c.id === engine.id)) {
                                const newCustom = customSearchEngines.filter(c => c.id !== engine.id);
                                setCustomSearchEngines(newCustom);
                                localStorage.setItem('tab365_customSearchEngines', JSON.stringify(newCustom));
                              }
                              // Also remove from selected engines so it disappears from the dropdown
                              if (selectedSearchEngineIds.includes(engine.id)) {
                                const newSelected = selectedSearchEngineIds.filter(id => id !== engine.id);
                                setSelectedSearchEngineIds(newSelected);
                                localStorage.setItem('tab365_selectedSearchEngines', JSON.stringify(newSelected));
                              } else if (selectedSearchEngineIds.length === 0) {
                                // If the user hasn't customized yet, we are showing the first 6 defaults.
                                // We should initialize the selected ids without this one.
                                const newSelected = allAvailableEngines.slice(0, 6).map(e => e.id).filter(id => id !== engine.id);
                                setSelectedSearchEngineIds(newSelected);
                                localStorage.setItem('tab365_selectedSearchEngines', JSON.stringify(newSelected));
                              }
                            }}>
                              <X size={12} />
                            </div>
                            {engine.icon ? (
                              typeof engine.icon === 'string' ? (
                                <img src={engine.icon} alt={engine.name} style={{ width: 28, height: 28, borderRadius: '50%' }} />
                              ) : (
                                <engine.icon size={28} color="currentColor" />
                              )
                            ) : (
                              <Globe size={28} color="currentColor" />
                            )}
                            <span className="search-engine-name">{engine.name}</span>
                          </div>
                        ))}
                        <div 
                          className="search-engine-item add-btn" 
                          title="添加"
                          onClick={() => {
                            setIsSearchDropdownOpen(false);
                            setIsSearchEngineModalOpen(true);
                          }}
                          style={{ color: settings.themeMode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)' }}
                        >
                          <div className="add-icon-circle">
                            <Plus size={18} color="currentColor" />
                          </div>
                          <span className="search-engine-name">添加</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <SearchEngineModal
                  isOpen={isSearchEngineModalOpen}
                  onClose={() => setIsSearchEngineModalOpen(false)}
                  availableEngines={allAvailableEngines}
                  selectedIds={validSelectedIds}
                  onAddCustomEngine={(url) => {
                    try {
                      let hostname = '自定义';
                      try {
                        hostname = new URL(url).hostname;
                      } catch (e) {
                        // ignore
                      }
                      const newId = `custom_se_${Date.now()}`;
                      const newEngine = {
                        id: newId,
                        name: hostname,
                        url: url,
                        icon: `https://api.iowen.cn/favicon/${hostname}.png`
                      };
                      const newCustom = [...customSearchEngines, newEngine];
                      setCustomSearchEngines(newCustom);
                      localStorage.setItem('tab365_customSearchEngines', JSON.stringify(newCustom));
                      
                      const currentSelected = validSelectedIds;
                      if (currentSelected.length < 6) {
                        const newSelected = [...currentSelected, newId];
                        setSelectedSearchEngineIds(newSelected);
                        localStorage.setItem('tab365_selectedSearchEngines', JSON.stringify(newSelected));
                      }
                    } catch (error) {
                      console.error("Failed to add custom engine:", error);
                    }
                  }}
                  onToggle={(id) => {
                    setSelectedSearchEngineIds(prev => {
                      const current = validSelectedIds;
                      let newSelection;
                      if (current.includes(id)) {
                        newSelection = current.filter(existingId => existingId !== id);
                      } else {
                        if (current.length >= 6) return current;
                        newSelection = [...current, id];
                      }
                      localStorage.setItem('tab365_selectedSearchEngines', JSON.stringify(newSelection));
                      return newSelection;
                    });
                  }}
                />

                <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                  <input 
                    ref={searchBoxRef}
                    type="text" 
                    className="search-box" 
                    placeholder="" 
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    onChange={(e) => setHasSearchValue(e.target.value.length > 0)}
                    onInput={(e) => setHasSearchValue(e.target.value.length > 0)}
                    onKeyDown={handleSearch}
                    style={{ paddingRight: '80px' }}
                  />
                  {!isSearchFocused && !hasSearchValue && rollingHotSearches.length > 0 && (
                    <div style={{ position: 'absolute', left: '72px', top: 0, bottom: 0, display: 'flex', alignItems: 'center', pointerEvents: 'none', right: '80px', overflow: 'hidden' }}>
                      <AnimatePresence mode="popLayout">
                        <motion.div
                          key={currentRollingIndex}
                          className="rolling-hot-search"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -20, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}
                        >
                          {rollingHotSearches[currentRollingIndex]?.title}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  )}
                  <div 
                    className="search-actions"
                    style={{ position: 'absolute', right: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {hasSearchValue && (
                      <div 
                        className="search-clear-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (searchBoxRef.current) {
                            searchBoxRef.current.value = '';
                            setHasSearchValue(false);
                            searchBoxRef.current.focus();
                          }
                        }}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', background: 'var(--bg-hover, rgba(255,255,255,0.1))' }}
                      >
                         <X size={16} color="currentColor" />
                      </div>
                    )}
                    {(settings.enableSearchHistory !== false || settings.enableGuessSearch !== false || settings.enableHotSearchRanking !== false) && (
                      <div 
                        className="search-history-toggle" 
                        onClick={(e) => {
                          e.stopPropagation();
                          const nextState = !isSearchHistoryOpen;
                          setIsSearchHistoryOpen(nextState);
                          if (nextState) { setIsSearchDropdownOpen(false); setIsRecommendDropdownOpen(false); }
                        }}
                      >
                         <ChevronDown size={18} color="currentColor" />
                      </div>
                    )}
                    <div 
                      className="search-action-btn"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => {
                        const val = document.querySelector('.search-box').value.trim();
                        if (val !== '') {
                          if (!searchHistory.includes(val)) {
                              const newHistory = [val, ...searchHistory].slice(0, 10);
                              setSearchHistory(newHistory);
                              localStorage.setItem('tab365_search_history', JSON.stringify(newHistory));
                          }
                          setIsSearchHistoryOpen(false);
                          const targetEngine = activeEngine || aiEngines[0];
                          recordActionLog('perform_search', 'search_engine', targetEngine.id, { keyword: val, name: targetEngine.name }, targetEngine.name);
                          window.open(generateSearchUrl(targetEngine, val), '_blank');
                        } else if (rollingHotSearches.length > 0) {
                          const targetSearch = rollingHotSearches[currentRollingIndex];
                          if (targetSearch && targetSearch.url) {
                            const rollingItemId = targetSearch.id || `rolling_${currentRollingIndex + 1}`;
                            recordActionLog('click_hot_search', 'rolling_hot_search', rollingItemId, { url: targetSearch.url, name: targetSearch.title, platform: targetSearch.platform || 'rolling' }, targetSearch.title);
                            window.open(targetSearch.url, '_blank');
                          }
                        }
                      }}
                    >
                      <Search size={20} color="currentColor" />
                    </div>
                  </div>
                  <AnimatePresence>
                    {isSearchHistoryOpen && (
                      <motion.div 
                        className="search-dropdown-panel"
                        initial={{ opacity: 0, y: -10 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        exit={{ opacity: 0, y: -10 }} 
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(settings.enableSearchHistory !== false && searchHistory.length > 0) && (
                          <div className="search-history-section">
                            <div className="section-header">
                              <span>搜索历史</span>
                              <div 
                                className="clear-btn"
                                onClick={() => {
                                  setSearchHistory([]);
                                  localStorage.setItem('tab365_search_history', JSON.stringify([]));
                                }}
                              >
                                <Trash2 size={12} /> 清空历史
                              </div>
                            </div>
                            <div className="tags-container">
                              {searchHistory.map((item, index) => (
                                <div key={index} className="tag" onClick={() => {
                                  document.querySelector('.search-box').value = item;
                                  setHasSearchValue(true);
                                  const targetEngine = activeEngine || aiEngines[0];
                                  recordActionLog('perform_search', 'search_history', targetEngine.id, { keyword: item, name: targetEngine.name }, targetEngine.name);
                                  window.open(generateSearchUrl(targetEngine, item), '_blank');
                                }}>{item}</div>
                              ))}
                            </div>
                          </div>
                        )}
                        {settings.enableGuessSearch !== false && (
                          <div className="guess-search-section">
                          <div className="section-header">
                            <span>猜你想搜</span>
                          </div>
                          <div className="tags-container">
                            {guessSearches.map((item, index) => (
                              <div key={index} className="tag" onClick={() => {
                                document.querySelector('.search-box').value = item;
                                setHasSearchValue(true);
                                const targetEngine = activeEngine || aiEngines[0];
                                recordActionLog('perform_search', 'guess_search', targetEngine.id, { keyword: item, name: targetEngine.name }, targetEngine.name);
                                window.open(generateSearchUrl(targetEngine, item), '_blank');
                              }}>{item}</div>
                            ))}
                          </div>
                        </div>
                        )}
                        {settings.enableHotSearchRanking !== false && hotSearchRanking && (
                          <div className="hot-search-ranking-section" style={{ marginTop: '16px' }}>
                            <div className="section-header">
                              <span>热搜排行</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '12px' }}>
                              <div className="ranking-col" style={{ minWidth: 0 }}>
                                <div className="ranking-col-header">
                                  百度
                                </div>
                                <div className="ranking-col-list">
                                  {hotSearchRanking.baidu.map((item, i) => (
                                    <div key={i} title={item.title} onClick={() => {
                                      recordActionLog('click_hot_search', 'search_ranking', `baidu_${i + 1}`, { platform: 'baidu', rank: i + 1, url: item.url, name: item.title }, item.title);
                                      window.open(item.url, '_blank');
                                    }} className="hot-search-item">
                                      <span className={`hot-search-number ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span>
                                      <span className="hot-search-text">{item.title}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="ranking-col" style={{ minWidth: 0 }}>
                                <div className="ranking-col-header">
                                  今日头条
                                </div>
                                <div className="ranking-col-list">
                                  {hotSearchRanking.toutiao.map((item, i) => (
                                    <div key={i} title={item.title} onClick={() => {
                                      recordActionLog('click_hot_search', 'search_ranking', `toutiao_${i + 1}`, { platform: 'toutiao', rank: i + 1, url: item.url, name: item.title }, item.title);
                                      window.open(item.url, '_blank');
                                    }} className="hot-search-item">
                                      <span className={`hot-search-number ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span>
                                      <span className="hot-search-text">{item.title}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="ranking-col" style={{ minWidth: 0 }}>
                                <div className="ranking-col-header">
                                  微博
                                </div>
                                <div className="ranking-col-list">
                                  {hotSearchRanking.weibo.map((item, i) => (
                                    <div key={i} title={item.title} onClick={() => {
                                      recordActionLog('click_hot_search', 'search_ranking', `weibo_${i + 1}`, { platform: 'weibo', rank: i + 1, url: item.url, name: item.title }, item.title);
                                      window.open(item.url, '_blank');
                                    }} className="hot-search-item">
                                      <span className={`hot-search-number ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span>
                                      <span className="hot-search-text">{item.title}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              
              {settings.enableSearchRecommend !== false && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', padding: '0 16px', alignItems: 'center', position: 'relative', width: '100%', justifyContent: 'flex-start' }}>
                {searchRecommendShortcuts.slice(0, 6).map((site) => (
                  <div 
                    key={site.id}
                    onClick={() => {
                      recordActionLog('click_shortcut', 'search_recommend', site.id || site.name, { url: site.url, name: site.name }, site.name);
                      window.open(site.url, '_blank');
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '13px', padding: '4px 10px', borderRadius: '12px', background: 'var(--bg-hover, rgba(255,255,255,0.1))', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  >
                    {site.icon ? (typeof site.icon === 'string' ? <img src={site.icon} style={{ width: 16, height: 16, borderRadius: '50%' }} /> : <site.icon size={16} color="rgba(255,255,255,0.8)" />) : null}
                    <span>{site.name}</span>
                  </div>
                ))}
                <div style={{ flex: 1 }}></div>
                <div title="更多推荐" onClick={(e) => {
                  e.stopPropagation();
                  const nextState = !isRecommendDropdownOpen;
                  setIsRecommendDropdownOpen(nextState);
                  if (nextState) { setIsSearchDropdownOpen(false); setIsSearchHistoryOpen(false); }
                }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '50%', cursor: 'pointer', background: 'var(--bg-hover, rgba(255,255,255,0.1))', transition: 'background 0.2s' }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                  <ChevronDown size={16} color="rgba(255,255,255,0.8)" style={{ transition: 'transform 0.2s', transform: isRecommendDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                </div>
                
                <AnimatePresence>
                  {isRecommendDropdownOpen && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, y: -10 }} 
                      className="search-dropdown recommend-dropdown"
                      style={{ top: '100%', right: 'auto', left: 0, width: '100%', marginTop: '10px', position: 'absolute', zIndex: 1000, overflow: 'hidden' }}
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <div className="recommend-grid">
                        {searchRecommendShortcuts.slice(0, 12).map((site) => (
                          <div 
                            key={site.id} 
                            title={site.name}
                            onClick={() => {
                              recordActionLog('click_shortcut', 'search_recommend_more', site.id || site.name, { url: site.url, name: site.name }, site.name);
                              window.open(site.url, '_blank');
                              setIsRecommendDropdownOpen(false);
                            }}
                            className="recommend-item"
                          >
                            <div className="recommend-icon-wrapper">
                              {site.icon ? (typeof site.icon === 'string' ? <img src={site.icon} alt="icon" style={{ width: 20, height: 20, borderRadius: '50%' }} /> : <site.icon size={20} color="currentColor" />) : <Search size={20} color="currentColor" />}
                            </div>
                            <span className="recommend-text">{site.name}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              )}
            </div>
          </div>
          {!isCleanMode && renderTopWeather()}
        </div>


        {!isCleanMode && (
          <div className="pages-viewport">
          <div className="pages-wrapper" style={{ 
            transform: `translateY(-${virtualPage * 100}%)`,
            transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.645, 0.045, 0.355, 1)' : 'none'
          }}>
            {[menuItems[TOTAL_PAGES - 1], ...menuItems, menuItems[0]].map((item, pageIdx) => {
              if (item.id === 0) {
                return (
                  <div key={`page-0-${pageIdx}`} className="page-section" style={{ justifyContent: 'flex-start' }}>
                    <div 
                      className="grid-container" 
                      style={{ 
                        gap: `${rowGap}px ${columnGap}px`,
                        '--grid-row-gap': `${rowGap}px`,
                        '--grid-column-gap': `${columnGap}px`,
                        '--grid-item-width': `${gridItemWidth}px`,
                        '--grid-item-height': `${gridItemHeight}px`,
                        '--widget-2x2-height': `${widget2x2Height}px`
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const data = e.dataTransfer.getData('application/json');
                        if (data) {
                          try {
                            const widgetData = JSON.parse(data);
                            if (widgetData.source === 'library') {
                              setPageData(prev => {
                                const newPageItems = [...(prev[item.id] || [])];
                                newPageItems.push({ ...widgetData, id: `widget-${Date.now()}` });
                                return { ...prev, [item.id]: newPageItems };
                              });
                              setIsSettingsModalOpen(false);
                            }
                          } catch(err) {}
                        }
                      }}
                    >
                      {(pageData[0] || []).map((iconItem, index) => {
                        if (iconItem.type === 'widget') {
                          return (
                            <motion.div
                              layout={draggingId !== null}
                              whileHover={{ y: -5, transition: { duration: 0.2 } }}
                              custom={{ i: index, isPageTurn }}
                              variants={itemVariants}
                              initial="hidden"
                              animate={currentPage === item.id ? "visible" : "hidden"}
                              key={`widget-${iconItem.id}-${pageIdx}`}
                              draggable
                              onDragStart={(e) => handleDragStart(e, iconItem.id)}
                              onDragEnter={(e) => handleDragEnter(e, 0, iconItem.id)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => e.preventDefault()}
                              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetId: iconItem.id, targetType: 'item', pageId: item.id }); }}
                              className={`shortcut-item widget-item size-${iconItem.w || 2}x${iconItem.h || 2} ${draggingId === iconItem.id ? 'dragging' : ''}`}
                            >
                              <div className={`widget-body widget-${iconItem.widgetType || 'weather'}`} onClick={() => handleWidgetClick(iconItem)} style={{ 
                                padding: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                
                                <WidgetRenderer widgetType={iconItem.widgetType || 'weather'} size={`${iconItem.w || 2}x${iconItem.h || 2}`} widgetScale={widgetScale} title={iconItem.name} globalWeatherData={globalWeatherData} />
                              </div>
                              {settings.iconNameVisible && (
                                <span className="shortcut-name" style={{ 
                                  fontSize: `${currentFontSize}px`, 
                                  ...(settings.iconColor === 'rainbow' 
                                    ? { backgroundImage: 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
                                    : { color: settings.iconColor })
                                }}>{iconItem.name || getWidgetDefaultName(iconItem.widgetType)}</span>
                              )}
                            </motion.div>
                          );
                        }
                        
                        return (
                          <motion.div 
                            layout={draggingId !== null}
                            whileHover={{ y: -5, transition: { duration: 0.2 } }}
                            custom={{ i: index, isPageTurn }}
                            variants={itemVariants}
                            initial="hidden"
                            animate={currentPage === item.id ? "visible" : "hidden"}
                            key={`shortcut-${iconItem.id}-${pageIdx}`} 
                            draggable
                            onDragStart={(e) => handleDragStart(e, iconItem.id)}
                            onDragEnter={(e) => handleDragEnter(e, 0, iconItem.id)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => e.preventDefault()}
                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetId: iconItem.id, targetType: 'item', pageId: item.id }); }}
                            onClick={() => handleShortcutClick(iconItem)}
                            className={`shortcut-item ${draggingId === iconItem.id ? 'dragging' : ''}`} 
                          >
                            <div className="shortcut-icon" style={{ 
                              width: `${settings.iconSize}px`, 
                              height: `${settings.iconSize}px`,
                              borderRadius: settings.iconStyle === 'circle' ? '50%' : `${settings.iconRadius / 2}%`,
                              opacity: settings.iconOpacity / 100
                            }}>
                              <img src={iconItem.icon} alt={iconItem.name} onError={(e) => { e.target.style.display = 'none' }} />
                            </div>
                            {settings.iconNameVisible && (
                              <span className="shortcut-name" style={{ 
                                fontSize: `${currentFontSize}px`, 
                                ...(settings.iconColor === 'rainbow' 
                                  ? { backgroundImage: 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
                                  : { color: settings.iconColor })
                              }}>
                                {iconItem.name}
                              </span>
                            )}
                          </motion.div>
                        );
                      })}
                      {renderAddTile(0)}
                    </div>
                  </div>
                );
              } else {
                let pageShortcuts = pageData[item.id] || [];
                return (
                  <div key={`page-${item.id}-${pageIdx}`} className="page-section" style={{ justifyContent: 'flex-start' }}>
                      <div 
                        className="grid-container" 
                        style={{ 
                          gap: `${rowGap}px ${columnGap}px`,
                          '--grid-row-gap': `${rowGap}px`,
                          '--grid-column-gap': `${columnGap}px`,
                          '--grid-item-width': `${gridItemWidth}px`,
                          '--grid-item-height': `${gridItemHeight}px`,
                          '--widget-2x2-height': `${widget2x2Height}px`
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const data = e.dataTransfer.getData('application/json');
                          if (data) {
                            try {
                              const widgetData = JSON.parse(data);
                              if (widgetData.source === 'library') {
                                setPageData(prev => {
                                  const newPageItems = [...(prev[item.id] || [])];
                                  newPageItems.push({ ...widgetData, id: `widget-${Date.now()}` });
                                  return { ...prev, [item.id]: newPageItems };
                                });
                                setIsSettingsModalOpen(false);
                              }
                            } catch(err) {}
                          }
                        }}
                      >
                        {pageShortcuts.map((iconItem, index) => {
                          if (iconItem.type === 'widget') {
                            return (
                              <motion.div
                                layout={draggingId !== null}
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                custom={{ i: index, isPageTurn }}
                                variants={itemVariants}
                                initial="hidden"
                                animate={currentPage === item.id ? "visible" : "hidden"}
                                key={`widget-${iconItem.id}-${pageIdx}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, iconItem.id)}
                                onDragEnter={(e) => handleDragEnter(e, item.id, iconItem.id)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => e.preventDefault()}
                                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetId: iconItem.id, targetType: 'item', pageId: item.id }); }}
                                className={`shortcut-item widget-item size-${iconItem.w || 2}x${iconItem.h || 2} ${draggingId === iconItem.id ? 'dragging' : ''}`}
                              >
                                <div className={`widget-body widget-${iconItem.widgetType || 'weather'}`} onClick={() => setActiveWidgetPopup(iconItem.widgetType || 'weather')} style={{ 
                                  padding: 0,
                                  overflow: 'hidden',
                                  display: 'flex',
                                  flexDirection: 'column'
                                }}>
                                  
                                  {(!iconItem.widgetType || iconItem.widgetType === 'weather') ? (
                                    <WeatherWidget size={`${iconItem.w || 2}x${iconItem.h || 2}`} widgetScale={widgetScale} title={iconItem.name} />
                                  ) : (
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.6, padding: `${1.2 * widgetScale}rem` }}>
                                      <h3 style={{ margin: 0, marginBottom: 'auto', fontSize: `${1.1 * widgetScale}rem`, fontWeight: 500, opacity: 0.9 }}>
                                        {iconItem.name || '开发中'}
                                      </h3>
                                      <div style={{ marginBottom: '12px' }}>
                                        {(() => {
                                          let FoundIcon = null;
                                          AVAILABLE_WIDGETS.forEach(cat => {
                                            const found = cat.items.find(w => w.widgetType === iconItem.widgetType);
                                            if (found && found.icon) FoundIcon = found.icon;
                                          });
                                          return FoundIcon ? <FoundIcon size={48 * widgetScale} /> : <div style={{width: 48 * widgetScale, height: 48 * widgetScale}} />;
                                        })()}
                                      </div>
                                      <div style={{ fontSize: `${1 * widgetScale}rem` }}>该组件正在开发中</div>
                                    </div>
                                  )}
                                </div>
                                {settings.iconNameVisible && (
                                  <span className="shortcut-name" style={{ 
                                    fontSize: `${currentFontSize}px`, 
                                    ...(settings.iconColor === 'rainbow' 
                                      ? { backgroundImage: 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
                                      : { color: settings.iconColor })
                                  }}>{iconItem.name || getWidgetDefaultName(iconItem.widgetType)}</span>
                                )}
                              </motion.div>
                            );
                          }
                          
                          return (
                            <motion.div 
                              layout={draggingId !== null}
                              whileHover={{ y: -5, transition: { duration: 0.2 } }}
                              custom={{ i: index, isPageTurn }}
                              variants={itemVariants}
                              initial="hidden"
                              animate={currentPage === item.id ? "visible" : "hidden"}
                              key={`shortcut-${iconItem.id}-${pageIdx}`} 
                              draggable
                              onDragStart={(e) => handleDragStart(e, iconItem.id)}
                              onDragEnter={(e) => handleDragEnter(e, item.id, iconItem.id)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => e.preventDefault()}
                              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetId: iconItem.id, targetType: 'item', pageId: item.id }); }}
                              onClick={() => handleShortcutClick(iconItem)}
                              className={`shortcut-item ${draggingId === iconItem.id ? 'dragging' : ''}`} 
                            >
                                <div className="shortcut-icon" style={{ 
                                  width: `${settings.iconSize}px`, 
                                  height: `${settings.iconSize}px`,
                                  borderRadius: settings.iconStyle === 'circle' ? '50%' : `${settings.iconRadius / 2}%`,
                                  opacity: settings.iconOpacity / 100,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {typeof iconItem.icon === 'string' ? (
                                    <img src={iconItem.icon} alt={iconItem.name} onError={(e) => handleImageError(e, iconItem.name)} />
                                  ) : (
                                    iconItem.icon && <iconItem.icon 
                                      size={settings.themeMode === 'light' ? settings.iconSize : settings.iconSize * 0.6} 
                                      color={settings.themeMode === 'light' ? 'var(--text-color)' : '#fff'} 
                                    />
                                  )}
                                </div>
                              {settings.iconNameVisible && (
                                <span className="shortcut-name" style={{ 
                                  fontSize: `${currentFontSize}px`, 
                                  ...(settings.iconColor === 'rainbow' 
                                    ? { backgroundImage: 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
                                    : { color: settings.iconColor })
                                }}>
                                  {iconItem.name}
                                </span>
                              )}
                            </motion.div>
                          );
                        })}
                        {renderAddTile(item.id)}
                      </div>
                  </div>
                );
              }
            })}
          </div>
        </div>
        )}

        {settings.bottomMenuVisible !== false && !isCleanMode && (
          <div className={`bottom-menu dock-menu ${!isDockVisible ? 'hidden' : ''}`} style={{
            background: settings.themeMode === 'light' ? `rgba(0, 0, 0, ${settings.sidebarOpacity * 0.85 / 100})` : `rgba(255, 255, 255, ${settings.sidebarOpacity / 100})`
          }}>
          
          {settings.bottomMenuRecommendVisible !== false && (
            <div className="dock-section">
              <div className="dock-label" style={{ cursor: 'pointer' }} onClick={() => { setLibraryModalSource('dock'); setLibraryActiveTab('shortcut'); setIsLibraryModalOpen(true); }}>
                <div>推荐</div>
                <div>网站</div>
              </div>
              <div className="dock-icons">
                {dockItems.slice(0, settings.bottomMenuRecommendMax || 5).map((site, index) => (
                  <div key={index} className="dock-icon" title={site.name} onClick={() => handleShortcutClick(site)} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {typeof site.icon === 'string' ? (
                      <img src={site.icon} alt={site.name} onError={(e) => handleImageError(e, site.name)} />
                    ) : (
                      site.icon && <site.icon size={24} color="#333" />
                    )}
                  </div>
                ))}
                <div className="dock-icon add-btn" title="添加" onClick={() => { setLibraryModalSource('dock'); setLibraryActiveTab('shortcut'); setIsLibraryModalOpen(true); }}>
                  <Plus size={24} color="#666" />
                </div>
              </div>
            </div>
          )}
          
          {settings.bottomMenuRecommendVisible !== false && settings.bottomMenuRecentVisible !== false && (
            <div className="dock-divider"></div>
          )}
          
          {settings.bottomMenuRecentVisible !== false && (
            <div className="dock-section">
              <div className="dock-label">
                <div>最近</div>
                <div>点击</div>
              </div>
              <div className="dock-icons">
                {recentClicks.slice(0, settings.bottomMenuRecentMax || 5).map((site, index) => (
                  <div key={index} className="dock-icon" title={site.name} onClick={() => handleShortcutClick(site)} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {typeof site.icon === 'string' ? (
                      <img src={site.icon} alt={site.name} onError={(e) => handleImageError(e, site.name)} />
                    ) : (
                      site.icon && <site.icon size={24} color="#333" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="dock-divider"></div>
          <div className="dock-icon add-btn" title="隐藏菜单" onClick={() => setIsDockVisible(false)}>
            <ChevronDown size={28} color="#666" />
          </div>
        </div>
        )}
        
        {settings.bottomMenuVisible !== false && !isCleanMode && !isDockVisible && (
          <div className="dock-show-btn" onClick={() => setIsDockVisible(true)} title="显示菜单">
            <ChevronUp size={24} color="#fff" />
          </div>
        )}
      </main>

      {isLoginModalOpen && (
        <div className="modal-overlay" onClick={() => setIsLoginModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-close" onClick={() => setIsLoginModalOpen(false)}>
              <X size={24} />
            </div>
            
            <h2 className="modal-title">账号登录</h2>
            
            <div className="login-tabs">
              <div 
                className={`login-tab ${loginTab === 'phone' ? 'active' : ''}`}
                onClick={() => setLoginTab('phone')}
              >
                <Smartphone size={18} />
                <span>手机号登录</span>
              </div>
              <div 
                className={`login-tab ${loginTab === 'wechat' ? 'active' : ''}`}
                onClick={() => setLoginTab('wechat')}
              >
                <QrCode size={18} />
                <span>微信登录</span>
              </div>
            </div>

            <div className="login-body" style={{ height: '280px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
              <div className="phone-login" style={{ display: loginTab === 'phone' ? 'flex' : 'none', flex: 1, flexDirection: 'column', gap: '1.8rem' }}>
                <div className="input-group">
                  <input type="text" placeholder="请输入手机号码" className="login-input" />
                </div>
                <div className="input-group verification-group">
                  <input type="text" placeholder="请输入验证码" className="login-input" />
                  <button className="get-code-btn">获取验证码</button>
                </div>
                <button className="submit-login-btn">登录</button>
              </div>
              
              <div className="wechat-login" style={{ display: loginTab === 'wechat' ? 'flex' : 'none', flex: 1, flexDirection: 'column', padding: 0 }}>
                <div className="qrcode-placeholder" id="wechat-qrcode-container" style={{ width: '180px', height: '180px', minHeight: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 0 }}>
                  {!wechatConfig && <QrCode size={100} color="rgba(255,255,255,0.8)" />}
                </div>
              </div>

              <div className="login-terms" style={{ alignSelf: 'flex-start', marginTop: 'auto', marginLeft: '12px' }}>
                <input type="checkbox" id="terms" defaultChecked />
                <label htmlFor="terms">我已阅读并同意《用户协议》和《隐私政策》</label>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirm Modal */}
      {deleteConfirmGroupId !== null && (
        <div className="modal-overlay" style={{ zIndex: 1000 }} onClick={() => setDeleteConfirmGroupId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-close" onClick={() => setDeleteConfirmGroupId(null)}>
              <X size={18} />
            </div>
            <div className="confirm-modal-header">
              <AlertCircle size={20} color="#faad14" fill="#faad14" stroke="#1f1f1f" />
              <span>提示</span>
            </div>
            <div className="confirm-modal-body">
              此操作将会删除分组下的所有内容，是否继续？
            </div>
            <div className="confirm-modal-footer">
              <button className="confirm-btn-cancel" onClick={() => setDeleteConfirmGroupId(null)}>取消</button>
              <button className="confirm-btn-ok" onClick={() => {
                setMenuItems(menuItems.filter(m => m.id !== deleteConfirmGroupId));
                setIsDeleteGroupMode(false);
                if (currentPage === deleteConfirmGroupId) {
                  setCurrentPage(0);
                  setVirtualPage(1);
                }
                setDeleteConfirmGroupId(null);
              }}>确定</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Group Modal (Large Variant) */}
      {isAddGroupModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div 
            className={`settings-modal ${isAddGroupMaximized ? 'maximized' : ''}`}
            style={{ 
              width: isAddGroupMaximized ? '100vw' : '90vw', 
              maxWidth: isAddGroupMaximized ? 'none' : '1200px', 
              height: isAddGroupMaximized ? '100vh' : '85vh', 
              maxHeight: isAddGroupMaximized ? 'none' : '92vh', 
              border: '1px solid var(--border-color, rgba(255,255,255,0.1))', 
              borderRadius: isAddGroupMaximized ? '0' : '24px', 
              background: 'rgba(24, 24, 28, 0.85)',
              transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1), max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1), max-height 0.4s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="settings-sidebar" style={{ paddingTop: '46px' }}>
              <div className="settings-nav">
                {editingMenuId === null ? (
                  <>
                    <div className={`settings-nav-item ${addGroupTab === 'preset' ? 'active' : ''}`} onClick={() => setAddGroupTab('preset')}><Shapes size={18}/> 菜单类型</div>
                    <div className={`settings-nav-item ${addGroupTab === 'custom' ? 'active' : ''}`} onClick={() => setAddGroupTab('custom')}><Plus size={18}/> 自定义菜单</div>
                  </>
                ) : (
                  <div className="settings-nav-item active"><Shapes size={18}/> 编辑分组</div>
                )}
              </div>
            </div>
            <div className="settings-content-wrapper">
              <div className="settings-controls">
                <div className="settings-maximize" onClick={() => setIsAddGroupMaximized(!isAddGroupMaximized)} title={isAddGroupMaximized ? "还原" : "最大化"} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '6px', color: 'var(--text-secondary, rgba(255,255,255,0.7))', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-color, #fff)'; e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.15))' }} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, rgba(255,255,255,0.7))'; e.currentTarget.style.background = 'transparent' }}>
                  {isAddGroupMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </div>
                <div className="settings-close" onClick={() => { setIsAddGroupModalOpen(false); setEditingMenuId(null); setIsAddGroupMaximized(false); }} title="关闭" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '6px', color: 'var(--text-secondary, rgba(255,255,255,0.7))', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-color, #fff)'; e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.15))' }} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, rgba(255,255,255,0.7))'; e.currentTarget.style.background = 'transparent' }}>
                  <X size={20} />
                </div>
              </div>

              <div className="settings-panel-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
                {addGroupTab === 'preset' && editingMenuId === null ? (
                  <div className="preset-group-container" style={{ flex: 1, minHeight: 0, width: '100%', maxWidth: 'none', padding: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="preset-group-scroll-container custom-scrollbar hover-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'overlay', paddingRight: '24px', paddingLeft: '8px', marginLeft: '-8px', marginRight: '-24px', paddingTop: '32px' }}>
                      <div className="preset-group-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', alignContent: 'start', gap: '20px', padding: '16px 0 32px 0', overflow: 'visible' }}>
                        {presetMenus.map((preset) => {
                        const presetName = preset.name || preset.title; // 兼容旧逻辑
                        const isAlreadyAdded = menuItems.some(m => m.title === presetName);
                        const PresetIcon = preset.icon;
                        return (
                          <div 
                            key={preset.id} 
                            style={{ 
                              background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', 
                              borderRadius: '16px', 
                              padding: '20px', 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: '12px', 
                              boxSizing: 'border-box',
                              height: '190px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              opacity: 1,
                              border: '1px solid var(--border-color, rgba(255,255,255,0.02))'
                            }}
                            onMouseEnter={(e) => { 
                              e.currentTarget.style.transform = 'scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 0 0 1px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.2)';
                              e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.08))';
                            }}
                            onMouseLeave={(e) => { 
                              e.currentTarget.style.transform = 'none';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.background = 'var(--bg-hover-light, rgba(255,255,255,0.05))';
                            }}
                            onClick={() => {
                              if (isAlreadyAdded) {
                                const existingItem = menuItems.find(m => m.title === presetName);
                                if (existingItem) {
                                  const existingIdx = menuItems.findIndex(m => m.id === existingItem.id);
                                  setIsTransitioning(true);
                                  setCurrentPage(existingItem.id);
                                  setVirtualPage(existingIdx + 1);
                                  setIsAddGroupModalOpen(false);
                                  setIsAddGroupMaximized(false);
                                }
                                return;
                              }
                              if (menuItems.length >= MAX_MENU_ITEMS) {
                                alert('最多只能添加 8 个菜单分组，请先删除不需要的分组后再添加。');
                                return;
                              }
                              const nextId = menuItems.length > 0 ? Math.max(...menuItems.map(m => m.id)) + 1 : 0;
                              const newMenuItems = [...menuItems, { id: nextId, icon: preset.icon, title: presetName }];
                              setMenuItems(newMenuItems);
                              const shortcuts = (preset.layout_items || []).map((s, idx) => ({
                                ...s,
                                id: `${preset.id}-${Date.now()}-${idx}`
                              }));
                              const newPageData = { ...pageData, [nextId]: shortcuts };
                              setPageData(prev => ({ ...prev, [nextId]: shortcuts }));
                              setIsTransitioning(true);
                              setCurrentPage(nextId);
                              setVirtualPage(newMenuItems.length);
                              setIsAddGroupModalOpen(false);
                              setIsAddGroupMaximized(false);
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ width: '56px', height: '56px', background: 'var(--bg-hover, rgba(255, 255, 255, 0.08))', backdropFilter: 'blur(10px)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', flexShrink: 0, overflow: 'hidden' }}>
                                  {typeof PresetIcon === 'string' && (PresetIcon.startsWith('http') || PresetIcon.startsWith('data:') || PresetIcon.startsWith('/')) ? (
                                    <img src={PresetIcon} alt="" style={{width: '32px', height: '32px', objectFit: 'contain'}} />
                                  ) : (
                                    (() => {
                                      const FinalIcon = typeof PresetIcon === 'string' ? getIconByName(PresetIcon) : (PresetIcon || getIconByName("Folder"));
                                      return <FinalIcon size={24} color="var(--text-secondary)" />;
                                    })()
                                  )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
                                  <div style={{ fontWeight: 600, fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>{presetName}</div>
                                  {preset.tag && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{preset.tag}</div>}
                                </div>
                              </div>
                            </div>
                            <div title={preset.description || preset.desc} style={{ fontSize: '0.85rem', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1, cursor: 'help', marginTop: '4px', color: 'var(--text-muted)' }}>
                              {preset.description || preset.desc || '暂无详细描述'}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 'auto' }}>
                              <div 
                                style={{ padding: '6px 12px', background: isAlreadyAdded ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', color: isAlreadyAdded ? 'var(--text-muted, rgba(255,255,255,0.4))' : 'var(--text-color, #fff)', borderRadius: '14px', fontSize: '0.8rem', cursor: isAlreadyAdded ? 'not-allowed' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                                onMouseEnter={(e) => { if (!isAlreadyAdded) e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                                onMouseLeave={(e) => { if (!isAlreadyAdded) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                              >
                                {!isAlreadyAdded && <Plus size={14} />}
                                {isAlreadyAdded ? '已添加' : '添加'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="custom-group-container" style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ padding: '24px 32px' }}>
                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                        <div className="form-label" style={{ marginBottom: '12px', fontSize: '0.95rem', color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>选择图标</div>
                      <div className="icon-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '8px', marginBottom: 0 }}>
                        {AVAILABLE_ICONS.map((Icon, idx) => (
                          <div 
                            key={idx} 
                            className={`icon-cell ${selectedIconIndex === idx ? 'selected' : ''}`}
                            onClick={() => setSelectedIconIndex(idx)}
                            data-title={ICON_LABELS[AVAILABLE_ICON_NAMES[idx]] || AVAILABLE_ICON_NAMES[idx]}
                            style={{ 
                              aspectRatio: '1/1', 
                              borderRadius: '10px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              background: selectedIconIndex === idx ? '#3b82f6' : 'var(--bg-hover-light, rgba(255,255,255,0.03))',
                              color: selectedIconIndex === idx ? '#fff' : 'var(--text-secondary, rgba(255,255,255,0.6))',
                              border: selectedIconIndex === idx ? 'none' : '1px solid var(--border-color, rgba(255,255,255,0.05))',
                              boxShadow: selectedIconIndex === idx ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                            }}
                          >
                            <Icon size={20} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ width: '100%', maxWidth: '460px' }}>
                      <div className="form-label" style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>分组名称</div>
                      <input 
                        type="text" 
                        className="group-name-input"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        placeholder="请输入分组名称，如：美工设计"
                        maxLength={12}
                        style={{ 
                          width: '100%', 
                          boxSizing: 'border-box', 
                          background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', 
                          border: '1px solid var(--border-color, rgba(255,255,255,0.1))', 
                          padding: '12px 16px', 
                          borderRadius: '10px', 
                          color: 'var(--text-color, #fff)', 
                          outline: 'none',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s'
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.background = 'rgba(59, 130, 246, 0.05)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'var(--border-color, rgba(255,255,255,0.1))'; e.target.style.background = 'var(--bg-hover-light, rgba(255,255,255,0.05))'; }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newGroupName.trim()) {
                            if (editingMenuId !== null) {
                              setMenuItems(menuItems.map(m => m.id === editingMenuId ? { ...m, icon: AVAILABLE_ICONS[selectedIconIndex], title: newGroupName.trim() } : m));
                            } else {
                              if (menuItems.length >= MAX_MENU_ITEMS) {
                                alert('最多只能添加 8 个菜单分组，请先删除不需要的分组后再添加。');
                                return;
                              }
                              const nextId = menuItems.length > 0 ? Math.max(...menuItems.map(m => m.id)) + 1 : 0;
                              const newMenuItems = [...menuItems, { id: nextId, icon: AVAILABLE_ICONS[selectedIconIndex], title: newGroupName.trim() }];
                              setMenuItems(newMenuItems);
                              setPageData(prev => ({ ...prev, [nextId]: [] }));
                              setIsTransitioning(true);
                              setCurrentPage(nextId);
                              setVirtualPage(newMenuItems.length);
                            }
                            setIsAddGroupModalOpen(false);
                            setIsAddGroupMaximized(false);
                            setNewGroupName('');
                            setSelectedIconIndex(0);
                            setEditingMenuId(null);
                          }
                        }}
                      />
                    </div>
                    <div className="add-group-actions" style={{ display: 'flex', gap: '16px', marginTop: '8px', width: '100%', maxWidth: '460px' }}>
                      <button 
                        className="btn-primary"
                        onClick={() => {
                          if (newGroupName.trim()) {
                            if (editingMenuId !== null) {
                              setMenuItems(menuItems.map(m => m.id === editingMenuId ? { ...m, icon: AVAILABLE_ICONS[selectedIconIndex], title: newGroupName.trim() } : m));
                            } else {
                              if (menuItems.length >= MAX_MENU_ITEMS) {
                                alert('最多只能添加 8 个菜单分组，请先删除不需要的分组后再添加。');
                                return;
                              }
                              const nextId = menuItems.length > 0 ? Math.max(...menuItems.map(m => m.id)) + 1 : 0;
                              const newMenuItems = [...menuItems, { id: nextId, icon: AVAILABLE_ICONS[selectedIconIndex], title: newGroupName.trim() }];
                              setMenuItems(newMenuItems);
                              setPageData(prev => ({ ...prev, [nextId]: [] }));
                              setIsTransitioning(true);
                              setCurrentPage(nextId);
                              setVirtualPage(newMenuItems.length);
                            }
                            setIsAddGroupModalOpen(false);
                            setIsAddGroupMaximized(false);
                            setNewGroupName('');
                            setSelectedIconIndex(0);
                            setEditingMenuId(null);
                          }
                        }}
                        style={{ flex: 1, padding: '14px', borderRadius: '12px', background: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.95rem', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        保存
                      </button>
                      <button 
                        className="btn-secondary"
                        onClick={() => {
                          setIsAddGroupModalOpen(false);
                          setIsAddGroupMaximized(false);
                          setNewGroupName('');
                          setSelectedIconIndex(0);
                          setEditingMenuId(null);
                        }}
                        style={{ flex: 1, padding: '14px', borderRadius: '12px', background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', color: 'var(--text-color, #fff)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', cursor: 'pointer', fontWeight: 500, fontSize: '0.95rem', transition: 'all 0.2s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.1))'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-hover-light, rgba(255,255,255,0.05))'; e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        取消
                      </button>
                    </div>
                  </div>
                  </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom Context Menu */}
      {contextMenu.visible && (
        <div 
          className="custom-context-menu"
          style={{ 
            left: contextMenu.x, 
            top: contextMenu.y, 
            transform: (contextMenu.targetType === 'group' && settings.sidebarPosition === 'right') 
              ? 'translateX(calc(-100% - 10px))' 
              : 'translateX(10px)' 
          }}
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          {contextMenu.targetType === 'group' ? (
            <>
              <div 
                className="context-menu-item"
                style={{ textAlign: 'left' }}
                onClick={() => {
                  const target = menuItems.find(m => m.id === contextMenu.targetId);
                  if (target) {
                    setEditingMenuId(target.id);
                    setNewGroupName(target.title);
                    const targetName = getIconName(target.icon);
                    let iconIdx = AVAILABLE_ICON_NAMES.findIndex(name => name.toLowerCase() === targetName.toLowerCase());
                    if (iconIdx === -1) {
                      iconIdx = AVAILABLE_ICONS.findIndex(icon => icon === target.icon);
                    }
                    setSelectedIconIndex(iconIdx !== -1 ? iconIdx : 0);
                    setAddGroupTab('custom');
                    setIsAddGroupModalOpen(true);
                  }
                  closeContextMenu();
                }}
              >
                编辑分组
              </div>
              <div 
                className="context-menu-item delete"
                style={{ textAlign: 'left' }}
                onClick={() => {
                  setMenuItems(menuItems.filter(m => m.id !== contextMenu.targetId));
                  if (currentPage === contextMenu.targetId) {
                    setIsTransitioning(false);
                    setCurrentPage(0);
                    setVirtualPage(1);
                  }
                  closeContextMenu();
                }}
              >
                删除分组
              </div>
            </>
          ) : (
            <>
              {(() => {
                const targetItems = pageData[contextMenu.pageId] || [];
                const currentItem = targetItems.find(i => i.id === contextMenu.targetId);
                const isWidget = currentItem && currentItem.type === 'widget';
                const currentW = currentItem ? (currentItem.w || (isWidget ? 2 : 1)) : 1;
                const currentH = currentItem ? (currentItem.h || (isWidget ? 2 : 1)) : 1;
                return (
                  <>
                    {isWidget && (
                      <div className="context-menu-section" style={{ padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, rgba(255,255,255,0.5))', marginBottom: '8px', textAlign: 'left' }}>布局</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {[{w: 1, h: 1}, {w: 2, h: 1}, {w: 2, h: 2}, {w: 4, h: 2}].map(size => {
                            const isSelected = currentW === size.w && currentH === size.h;
                            return (
                              <div 
                                key={`${size.w}x${size.h}`}
                                style={{ 
                                  padding: '4px 8px', 
                                  background: isSelected ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)', 
                                  borderRadius: '4px', 
                                  fontSize: '0.8rem', 
                                  cursor: isSelected ? 'default' : 'pointer',
                                  fontWeight: isSelected ? 'bold' : 'normal',
                                  color: isSelected ? '#fff' : 'inherit'
                                }}
                                onClick={() => {
                                  if (isSelected) return;
                                  setPageData(prev => {
                                    const newPageData = { ...prev };
                                    const pageItems = newPageData[contextMenu.pageId];
                                    const itemIndex = pageItems.findIndex(i => i.id === contextMenu.targetId);
                                    if (itemIndex > -1) {
                                      pageItems[itemIndex] = { ...pageItems[itemIndex], w: size.w, h: size.h };
                                    }
                                    return newPageData;
                                  });
                                  closeContextMenu();
                                }}
                              >
                                {size.w}x{size.h}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    <div className="context-menu-item" style={{ textAlign: 'left' }} onClick={closeContextMenu}>编辑图标</div>
                    <div 
                      className="context-menu-item delete"
                      style={{ textAlign: 'left' }}
                      onClick={() => {
                        setPageData(prev => {
                          const newPageData = { ...prev };
                          newPageData[contextMenu.pageId] = newPageData[contextMenu.pageId].filter(i => i.id !== contextMenu.targetId);
                          return newPageData;
                        });
                        closeContextMenu();
                      }}
                    >
                      删除图标
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* Settings Modal */}
      <AnimatePresence>
        {isSettingsModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="modal-overlay settings-overlay" 
            onWheel={(e) => e.stopPropagation()}
          >
            <motion.div 
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="settings-modal-wrapper"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`settings-modal ${isSettingsMaximized ? 'maximized' : ''}`}>
                {!isSettingsMaximized && (
                  <div className="settings-sidebar">
                    <div className="settings-sidebar-header" style={{ paddingBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => {
                    if (userInfo) {
                      setSettingsActiveTab('account');
                    } else {
                      setIsLoginModalOpen(true);
                    }
                  }}>
                    <div style={{ width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', overflow: 'hidden' }}>
                      <img 
                        src={userInfo ? (userInfo.avatar || '/chicken.png') : '/chicken.png'} 
                        alt="user" 
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                          transform: (userInfo && userInfo.avatar) ? 'none' : 'scale(1.15)',
                          filter: (userInfo && userInfo.avatar) ? 'none' : 'brightness(0) invert(1)'
                        }} 
                      />
                    </div>
                    {userInfo ? (
                      <div style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '15px', color: 'var(--text-color, #fff)', fontWeight: 500 }}>
                        {userInfo.nickname || `用户`}
                      </div>
                    ) : (
                      <div className="settings-login-btn">
                        登录/注册
                      </div>
                    )}
                  </div>
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '0 16px 16px 16px' }}></div>
                  <div className="settings-nav">

                    <div className={`settings-nav-item ${settingsActiveTab === 'icons' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('icons')}><LayoutGrid size={18}/> 图标</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'searchEngine' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('searchEngine')}><Search size={18}/> 搜索引擎</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'theme' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('theme')}><Palette size={18}/> 主题</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'wallpaper' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('wallpaper')}><Image size={18}/> 壁纸</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'sidebar' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('sidebar')}><Sidebar size={18}/> 侧边栏</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'bottomMenu' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('bottomMenu')}><PanelBottom size={18}/> 底部栏</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'quickSettings' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('quickSettings')}><Settings size={18}/> 快捷设置</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'backup' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('backup')}><Cloud size={18}/> 备份与恢复</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'reset' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('reset')}><RotateCcw size={18}/> 重置设置</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'feedback' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('feedback')}><MessageSquare size={18}/> 问题与建议</div>
                    <div className={`settings-nav-item ${settingsActiveTab === 'about' ? 'active' : ''}`} onClick={() => setSettingsActiveTab('about')}><Info size={18}/> 关于</div>
                  </div>
                </div>
                )}
                <div className="settings-content-wrapper">
                  <div className="settings-controls">

                    <div className="settings-close" onClick={() => setIsSettingsModalOpen(false)} title="关闭">
                      <X size={20} />
                    </div>
                  </div>
                  <SettingsContent activeTab={settingsActiveTab} settings={settings} updateSetting={updateSetting} setIsSettingsModalOpen={setIsSettingsModalOpen} menuItems={menuItems} pageData={pageData} setPageData={setPageData} userInfo={userInfo} setAuthToken={setAuthToken} setUserInfo={setUserInfo} isMaximized={isSettingsMaximized} customSearchEngines={customSearchEngines} setCustomSearchEngines={setCustomSearchEngines} onResetAll={handleResetAllToDefault} />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {syncConflict && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100000
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              style={{
                background: '#1a1a1a', borderRadius: '16px', width: 'auto', minWidth: '450px',
                border: '1px solid var(--border-color, rgba(255,255,255,0.1))', overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div style={{ position: 'relative', padding: '24px 24px 16px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Cloud size={24} color="#3b82f6" />
                  <span style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-color, #fff)' }}>发现配置冲突</span>
                </div>
              </div>
              <div style={{ padding: '0 24px 24px 24px' }}>
                <p style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', fontSize: '15px', margin: '0 0 20px 0', lineHeight: '1.6', textAlign: 'center' }}>
                  检测到当前设备的本地桌面排版与您的云端备份存在差异。请选择要保留的配置：
                </p>
                
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                    <span style={{ color: 'var(--text-muted, rgba(255,255,255,0.5))' }}>云端最后修改:</span>
                    <span style={{ color: 'var(--text-color, #fff)', fontWeight: 500 }}>{new Date(syncConflict.cloudTimestamp).toLocaleString('zh-CN')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                    <span style={{ color: 'var(--text-muted, rgba(255,255,255,0.5))' }}>本地最后修改:</span>
                    <span style={{ color: 'var(--text-color, #fff)', fontWeight: 500 }}>{new Date(syncConflict.localTimestamp).toLocaleString('zh-CN')}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <div 
                    style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-color, #fff)', cursor: 'pointer', fontSize: '14px', fontWeight: 500, textAlign: 'center', transition: 'background 0.2s' }}
                    onClick={async (e) => {
                      const btn = e.currentTarget;
                      const originalText = btn.innerText;
                      btn.innerText = '保存中...';
                      btn.style.pointerEvents = 'none';
                      const localDataStr = localStorage.getItem(`tab365_visitor_backup_${uid}`);
                      if (localDataStr) {
                        try {
                          const localData = JSON.parse(localDataStr);
                          localData.timestamp = Date.now(); // 强制更新为最新时间
                          const updatedDataStr = JSON.stringify(localData);
                          localStorage.setItem(`tab365_visitor_backup_${uid}`, updatedDataStr);
                          
                          await fetch('/api/v1/sync/backup/upload', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                            body: updatedDataStr
                          });
                        } catch(e) {}
                      }
                      setSyncConflict(null);
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    保留本地配置
                  </div>
                  <div 
                    style={{ flex: 1, padding: '12px', borderRadius: '12px', background: '#3b82f6', color: 'var(--text-color, #fff)', cursor: 'pointer', fontSize: '14px', fontWeight: 500, textAlign: 'center', transition: 'background 0.2s' }}
                    onClick={() => {
                      syncConflict.applyCloudData();
                      setSyncConflict(null);
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}
                  >
                    使用云端配置
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>



      <AnimatePresence>
        {isLibraryModalOpen && (
          <LibraryModal 
            activeTab={libraryActiveTab} 
            setActiveTab={setLibraryActiveTab} 
            onClose={() => setIsLibraryModalOpen(false)} 
            menuItems={menuItems} 
            pageData={pageData} 
            setPageData={setPageData}
            dockItems={dockItems}
            setDockItems={setDockItems}
            currentPageId={menuItems[currentPage]?.id}
            maxDockItems={settings.bottomMenuRecommendMax || 5}
            source={libraryModalSource}
            onAddSearchEngine={(shortcut) => {
              if (!customSearchEngines.find(c => c.id === shortcut.id)) {
                const newCustom = [...customSearchEngines, shortcut];
                setCustomSearchEngines(newCustom);
                localStorage.setItem('tab365_customSearchEngines', JSON.stringify(newCustom));
              }
            }}
          />
        )}
      </AnimatePresence>

    </div>
  )
}

export default App
